// TodoList.js
import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import AppLayout from '../components/AppLayout'
import TaskModal from '../components/TaskModal'
import InviteModal from '../components/InviteModal'
import { useApp } from '../context/AppContext'
import { storage } from '../firebase'
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage'

export function TodoList() {
  const { eventId } = useParams()
  const { events, tasks, toggleTask, deleteTask, canDelete } = useApp()
  const [filter, setFilter] = useState('all')
  const [taskModal, setTaskModal] = useState(null)

  const event = events && events.find(e => e.id === eventId)
  const eventTasks = tasks.filter(t => t.eventId === eventId)

  const filtered = eventTasks.filter(t => {
    if (filter === 'high') return t.priority === 'high' && !t.done
    if (filter === 'overdue') return !t.done && t.deadline && new Date(t.deadline) < new Date()
    if (filter === 'done') return t.done
    return true
  }).sort((a, b) => {
    if (a.done !== b.done) return a.done ? 1 : -1
    return new Date(b.createdAt) - new Date(a.createdAt)
  })

  return (
    <AppLayout>
      <div className="page-header">
        <div className="page-header-row1">
          <div className="page-header-left">
            <h1 className="page-title">{event?.name}</h1>
            <span className="page-subtitle">Opgaver</span>
          </div>
        </div>
        <div className="filter-row">
          {[
            { key: 'all', label: `alle · ${eventTasks.length}` },
            { key: 'high', label: 'høj prioritet' },
            { key: 'overdue', label: 'forsinkede' },
            { key: 'done', label: 'klaret' },
          ].map(f => (
            <button key={f.key} className={`filter-pill ${filter === f.key ? 'active' : ''}`} onClick={() => setFilter(f.key)}>
              {f.label}
            </button>
          ))}
        </div>
      </div>

      <div className="page-content">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <h2 style={{ fontSize: 18, fontWeight: 700, color: '#1a1a2e' }}>Opgaver</h2>
          <button className="btn-primary" onClick={() => setTaskModal('new')}>
            <svg width="11" height="11" viewBox="0 0 11 11" fill="none"><path d="M5.5 1v9M1 5.5h9" stroke="white" strokeWidth="1.5" strokeLinecap="round"/></svg>
            ny opgave
          </button>
        </div>
        <div className="card">
          {filtered && filtered.map(task => (
            <div key={task.id} className="todo-item" onClick={() => setTaskModal(task)}>
              <div
                className={`checkbox ${task.done ? 'checked' : ''}`}
                style={{ width: 20, height: 20 }}
                onClick={e => { e.stopPropagation(); toggleTask(task.id) }}
              >
                {task.done && <svg width="9" height="7" viewBox="0 0 9 7" fill="none"><path d="M1 3.5l2.5 2.5 4.5-5" stroke="white" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/></svg>}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className={`todo-text ${task.done ? 'done' : ''}`}>{task.title}</div>
                <div className="todo-meta">
                  <span className={`pill pill-${task.priority}`}>{task.priority === 'high' ? 'høj' : task.priority === 'medium' ? 'middel' : 'lav'}</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <div style={{ width: 14, height: 14, borderRadius: '50%', background: '#f0eeff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 8, fontWeight: 700, color: '#5248cc' }}>
                      {task.assignedTo.split(' ').map(n => n[0]).join('')}
                    </div>
                    <span style={{ fontSize: 10, color: '#DF83A9' }}>{task.assignedTo}</span>
                  </div>
                  {task.deadline && <span style={{ fontSize: 10, color: '#DF83A9' }}>· {task.deadline}{task.deadlineTime ? ` kl. ${task.deadlineTime}` : ''}</span>}
                  {task.done && <span className="pill pill-done">klaret</span>}
                  {!task.done && task.deadline && new Date(task.deadline) < new Date() && <span className="pill pill-overdue">forsinket</span>}
                </div>
              </div>
              {canDelete(eventId) && <button onClick={e => { e.stopPropagation(); deleteTask(eventId, task.id) }} style={{ padding: '4px 8px', fontSize: 11, color: '#DF83A9', background: 'none', border: 'none', cursor: 'pointer', whiteSpace: 'nowrap' }}>slet</button>}
            </div>
          ))}
          {filtered.length === 0 && (
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
              <p style={{ fontSize: 13, color: '#DF83A9' }}>ingen opgaver her endnu</p>
              <button className="btn-primary" style={{ margin: '12px auto 0', display: 'inline-flex' }} onClick={() => setTaskModal('new')}>opret første opgave</button>
            </div>
          )}
        </div>
      </div>

      {taskModal && taskModal !== 'new' && <TaskModal task={taskModal} onClose={() => setTaskModal(null)} />}
      {taskModal === 'new' && <TaskModal defaultEventId={eventId} onClose={() => setTaskModal(null)} />}
    </AppLayout>
  )
}

// Budget.js
export function Budget() {
  const { eventId } = useParams()
  const { events, budget, addBudgetItem, updateEvent, canDelete } = useApp()
  const [openCat, setOpenCat] = useState(null)
  const [addForm, setAddForm] = useState(null)
  const [addBudgetModal, setAddBudgetModal] = useState(false)
  const [selectedCategory, setSelectedCategory] = useState(null)
  const [editBudgetModal, setEditBudgetModal] = useState(false)
  const [totalBudgetInput, setTotalBudgetInput] = useState('')
  const [deleteConfirmModal, setDeleteConfirmModal] = useState(null)

  const budgetCategories = [
    { name: 'Mad', color: '#923232', bgColor: '#ffe4e4' },
    { name: 'Drikkevarer', color: '#279089', bgColor: '#e8f7f6' },
    { name: 'Dekoration', color: '#b89a09', bgColor: '#fff9e6' },
    { name: 'Stole & Borde', color: '#559c8f', bgColor: '#e8f9f5' },
    { name: 'Invitationer', color: '#4b5b9c', bgColor: '#f3f2ff' },
    { name: 'Musik & Underholdning', color: '#a05e64', bgColor: '#ffefef' },
    { name: 'Transport', color: '#5b8978', bgColor: '#f0faf8' },
    { name: 'Blomster', color: '#744d3d', bgColor: '#ffe8d6' },
  ]

  const event = events && events.find(e => e.id === eventId)
  const cats = budget[eventId] || []
  const totalBudget = event?.totalBudget || 0
  const totalSpent = cats.reduce((s, c) => s + c.items.reduce((si, i) => si + i.amount, 0), 0)
  const pct = totalBudget > 0 ? Math.round((totalSpent / totalBudget) * 100) : 0

  return (
    <AppLayout>
      <div className="page-header">
        <div className="page-header-row1">
          <div className="page-header-left">
            <h1 className="page-title">{event?.name}</h1>
            <span className="page-subtitle">Budget</span>
          </div>
        </div>
      </div>

      <div className="page-content">
        <div className="stats-grid" style={{ gridTemplateColumns: 'repeat(3,1fr)' }}>
          <div className="stat-card" style={{ background: '#fff', cursor: 'pointer', position: 'relative' }} onClick={() => { setEditBudgetModal(true); setTotalBudgetInput(totalBudget.toString()) }}>
            <div className="stat-label" style={{ color: '#DF83A9' }}>samlet budget</div>
            <div className="stat-value">{totalBudget.toLocaleString('da-DK')} kr</div>
            <button className="btn-ghost" style={{ position: 'absolute', top: 8, right: 8, fontSize: 11 }} onClick={(e) => { e.stopPropagation(); setEditBudgetModal(true); setTotalBudgetInput(totalBudget.toString()) }}>Rediger</button>
          </div>
          <div className="stat-card" style={{ background: '#ffe4e4' }}><div className="stat-label" style={{ color: '#cc2020' }}>brugt</div><div className="stat-value">{totalSpent.toLocaleString('da-DK')} kr</div><div className="stat-sub" style={{ color: '#cc2020' }}>{pct}%</div></div>
          <div className="stat-card" style={{ background: '#e8fdf5' }}><div className="stat-label" style={{ color: '#007a58' }}>tilbage</div><div className="stat-value">{(totalBudget - totalSpent).toLocaleString('da-DK')} kr</div></div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <h2 style={{ fontSize: 18, fontWeight: 700, color: '#1a1a2e' }}>Budget</h2>
          <p style={{ fontSize: 12, color: '#222222' }}>Automatisk fra indkøbsliste</p>
        </div>

        <div className="card">
          <div className="card-header">
            <span className="card-title">Kategorier</span>
            <span style={{ fontSize: 10, color: '#DF83A9' }}>klik for at udfolde</span>
          </div>
          {cats && cats.map(cat => {
            const catSpent = cat.items.reduce((s, i) => s + i.amount, 0)
            const catPct = cat.budget > 0 ? Math.round((catSpent / cat.budget) * 100) : 0
            const isOpen = openCat === cat.id
            return (
              <div key={cat.id} className="budget-cat" style={{ background: cat.bgColor }}>
                <div className="budget-cat-header" onClick={() => setOpenCat(isOpen ? null : cat.id)}>
                  <span className="budget-cat-name" style={{ color: cat.color }}>{cat.name}</span>
                  <div className="budget-bar-track" style={{ background: cat.color + '33' }}>
                    <div className="budget-bar-fill" style={{ width: `${catPct}%`, background: cat.color }} />
                  </div>
                  <span className="budget-amount" style={{ color: cat.color }}>{catSpent.toLocaleString('da-DK')} kr</span>
                  <svg className={`budget-chevron ${isOpen ? 'open' : ''}`} style={{ color: cat.color }} viewBox="0 0 8 8" fill="none">
                    <path d="M2 3l2 2 2-2" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
                  </svg>
                </div>
                {isOpen && (
                  <div className="budget-items">
                    {cat.items && cat.items.map(item => (
                      <div key={item.id} className="budget-item-row" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span className="budget-item-name">{item.name}</span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                          <span style={{ fontWeight: 600, color: cat.color }}>{item.amount.toLocaleString('da-DK')} kr</span>
                          {canDelete(eventId) && (
                            <button
                              onClick={() => setDeleteConfirmModal({ itemId: item.id, itemName: item.name })}
                              style={{ background: 'none', border: 'none', color: '#cc2020', cursor: 'pointer', fontSize: 14, padding: 0 }}
                            >
                              ×
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                    {cat.items.length === 0 && <p style={{ fontSize: 11, color: '#DF83A9', padding: '4px 0' }}>ingen udgifter endnu</p>}
                    {addForm?.catId === cat.id ? (
                      <div className="budget-add-row">
                        <input className="budget-add-input" placeholder="navn på udgift" value={addForm.name} onChange={e => setAddForm({ ...addForm, name: e.target.value })} />
                        <input className="budget-add-input" style={{ width: 80 }} placeholder="beløb" type="number" value={addForm.amount} onChange={e => setAddForm({ ...addForm, amount: e.target.value })} />
                        <button className="budget-add-btn" style={{ color: cat.color }} onClick={() => { if (addForm.name && addForm.amount) { addBudgetItem(eventId, cat.id, addForm.name, addForm.amount); setAddForm(null) } }}>tilføj</button>
                        <button onClick={() => setAddForm(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#DF83A9', fontSize: 14 }}>×</button>
                      </div>
                    ) : (
                      <button onClick={() => setAddForm({ catId: cat.id, name: '', amount: '' })} style={{ fontSize: 12, fontWeight: 600, color: cat.color, background: 'none', border: 'none', cursor: 'pointer', marginTop: 6, fontFamily: 'inherit' }}>+ tilføj udgift</button>
                    )}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </div>

      {/* Edit Total Budget Modal */}
      {editBudgetModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setEditBudgetModal(false)}>
          <div className="modal">
            <div className="modal-header">
              <div><div className="modal-title">Rediger samlet budget</div></div>
              <button className="modal-close" onClick={() => setEditBudgetModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="input-group">
                <label className="input-label">Samlet budget (kr)</label>
                <input
                  className="input"
                  type="number"
                  placeholder="0"
                  value={totalBudgetInput}
                  onChange={e => setTotalBudgetInput(e.target.value)}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="modal-cancel" onClick={() => setEditBudgetModal(false)}>Annuller</button>
              <button
                className="modal-confirm"
                onClick={() => {
                  const newBudget = parseFloat(totalBudgetInput) || 0
                  updateEvent(eventId, { totalBudget: newBudget })
                  setEditBudgetModal(false)
                }}
              >
                Gem budget
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Budget Modal */}
      {addBudgetModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setAddBudgetModal(false)}>
          <div className="modal">
            <div className="modal-header">
              <div><div className="modal-title">tilføj vare</div></div>
              <button className="modal-close" onClick={() => { setAddBudgetModal(false); setSelectedCategory(null); setAddForm(null) }}>×</button>
            </div>
            <div className="modal-body">
              <div className="input-group" style={{ marginBottom: 16 }}>
                <label className="input-label">Kategori</label>
                <select
                  className="input"
                  style={{ appearance: 'none', cursor: 'pointer' }}
                  value={selectedCategory?.name || ''}
                  onChange={e => {
                    const cat = budgetCategories.find(c => c.name === e.target.value)
                    setSelectedCategory(cat)
                    setAddForm(null)
                  }}
                >
                  <option value="">– vælg en kategori –</option>
                  {budgetCategories.map(cat => (
                    <option key={cat.name} value={cat.name}>{cat.name}</option>
                  ))}
                </select>
              </div>

              {selectedCategory && (
                <>
                  <div className="input-group" style={{ marginBottom: 12 }}>
                    <label className="input-label">Navn på vare</label>
                    <input
                      className="input"
                      placeholder="fx. Serveringsfad"
                      value={addForm?.name || ''}
                      onChange={e => setAddForm({ catId: selectedCategory.name, name: e.target.value, amount: addForm?.amount || '' })}
                    />
                  </div>
                  <div className="input-group" style={{ marginBottom: 12 }}>
                    <label className="input-label">Beløb</label>
                    <input
                      className="input"
                      type="number"
                      placeholder="0 kr"
                      value={addForm?.amount || ''}
                      onChange={e => setAddForm({ catId: selectedCategory.name, name: addForm?.name || '', amount: e.target.value })}
                    />
                  </div>
                </>
              )}
            </div>
            <div className="modal-footer">
              <button className="modal-cancel" onClick={() => { setAddBudgetModal(false); setSelectedCategory(null); setAddForm(null) }}>Annuller</button>
              {selectedCategory && (
                <button
                  className="modal-confirm"
                  onClick={() => {
                    if (addForm?.name && addForm?.amount) {
                      const categoryId = selectedCategory.name
                      addBudgetItem(eventId, categoryId, addForm.name, parseFloat(addForm.amount))
                      setAddBudgetModal(false)
                      setSelectedCategory(null)
                      setAddForm(null)
                    }
                  }}
                >
                  Tilføj vare
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirmModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setDeleteConfirmModal(null)}>
          <div className="modal" style={{ maxWidth: 400 }}>
            <div className="modal-header">
              <div><div className="modal-title">Fjern vare</div></div>
              <button className="modal-close" onClick={() => setDeleteConfirmModal(null)}>×</button>
            </div>
            <div className="modal-body">
              <p style={{ fontSize: 14, color: '#1a1a2e', marginBottom: 16 }}>Er du sikker på du vil fjerne <strong>{deleteConfirmModal.itemName}</strong> fra budgettet?</p>
            </div>
            <div className="modal-footer">
              <button className="modal-cancel" onClick={() => setDeleteConfirmModal(null)}>Annuller</button>
              <button
                className="modal-cancel"
                style={{ color: '#cc2020', borderColor: '#cc2020' }}
                onClick={async () => {
                  try {
                    const fs = await import('../services/firestore')
                    await fs.deleteBudgetItemFirestore(eventId, deleteConfirmModal.itemId)
                    setDeleteConfirmModal(null)
                  } catch (error) {
                    console.error('Error deleting budget item:', error)
                    setDeleteConfirmModal(null)
                  }
                }}
              >
                Fjern
              </button>
            </div>
          </div>
        </div>
      )}

    </AppLayout>
  )
}

// Guests.js
export function Guests() {
  const { eventId } = useParams()
  const { events, guests, updateGuestStatus, addGuest, deleteGuest, canDelete } = useApp()
  const [filter, setFilter] = useState('all')
  const [addOpen, setAddOpen] = useState(false)
  const [form, setForm] = useState({ name: '', email: '', phone: '', dietary: '', plusOne: false, notes: '' })

  const event = events && events.find(e => e.id === eventId)
  const eventGuests = guests[eventId] || []
  const filtered = eventGuests.filter(g => filter === 'all' || g.status === filter).reverse()
  const confirmed = eventGuests.filter(g => g.status === 'confirmed').length
  const pending = eventGuests.filter(g => g.status === 'pending').length
  const declined = eventGuests.filter(g => g.status === 'declined').length

  const handleAdd = (e) => {
    e.preventDefault()
    addGuest(eventId, { ...form, eventId })
    setForm({ name: '', email: '', phone: '', dietary: '', plusOne: false, notes: '' })
    setAddOpen(false)
  }

  return (
    <AppLayout>
      <div className="page-header">
        <div className="page-header-row1">
          <div className="page-header-left">
            <h1 className="page-title">Gæsteliste</h1>
            <span className="page-subtitle">{event?.name}</span>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 12 }}>
          <div style={{ background: '#e8fdf5', borderRadius: 14, padding: '10px', textAlign: 'center' }}>
            <p style={{ fontSize: 20, fontWeight: 700, color: '#1a1a2e' }}>{confirmed}</p>
            <p style={{ fontSize: 10, fontWeight: 500, color: '#007a58' }}>bekræftet</p>
          </div>
          <div style={{ background: '#fff3e0', borderRadius: 14, padding: '10px', textAlign: 'center' }}>
            <p style={{ fontSize: 20, fontWeight: 700, color: '#1a1a2e' }}>{pending}</p>
            <p style={{ fontSize: 10, fontWeight: 500, color: '#9a5800' }}>afventer</p>
          </div>
          <div style={{ background: '#ffe4e4', borderRadius: 14, padding: '10px', textAlign: 'center' }}>
            <p style={{ fontSize: 20, fontWeight: 700, color: '#1a1a2e' }}>{declined}</p>
            <p style={{ fontSize: 10, fontWeight: 500, color: '#cc2020' }}>afbud</p>
          </div>
        </div>

        <div className="filter-row">
          {['all', 'confirmed', 'pending', 'declined'].map(f => (
            <button key={f} className={`filter-pill ${filter === f ? 'active' : ''}`} onClick={() => setFilter(f)}>
              {f === 'all' ? 'alle' : f === 'confirmed' ? 'bekræftet' : f === 'pending' ? 'afventer' : 'afbud'}
            </button>
          ))}
        </div>
      </div>

      <div className="page-content">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <h2 style={{ fontSize: 18, fontWeight: 700, color: '#1a1a2e' }}>Gæster</h2>
          <button className="btn-primary" onClick={() => setAddOpen(true)}>
            <svg width="11" height="11" viewBox="0 0 11 11" fill="none"><path d="M5.5 1v9M1 5.5h9" stroke="white" strokeWidth="1.5" strokeLinecap="round"/></svg>
            tilføj gæst
          </button>
        </div>
        <div className="card">
          {filtered && filtered.map(guest => (
            <div key={guest.id} className="guest-row">
              <div className="avatar" style={{ width: 38, height: 38, background: guest.status === 'confirmed' ? '#e8fdf5' : guest.status === 'pending' ? '#fff3e0' : '#ffe4e4', color: guest.status === 'confirmed' ? '#007a58' : guest.status === 'pending' ? '#9a5800' : '#cc2020', fontSize: 12 }}>
                {guest.name.split(' ').map(n => n[0]).join('').slice(0, 2)}
              </div>
              <div className="guest-info">
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                  <span className="guest-name">{guest.name}</span>
                  {guest.plusOne && <span className="pill pill-purple">+ ledsager</span>}
                </div>
                {guest.email && <p className="guest-email">{guest.email}</p>}
                {guest.dietary && <p className="guest-dietary">{guest.dietary}</p>}
              </div>
              <div className="guest-status-btns">
                <button className={`status-btn status-btn-yes ${guest.status === 'confirmed' ? 'active' : ''}`} onClick={() => updateGuestStatus(eventId, guest.id, 'confirmed')}>ja</button>
                <button className={`status-btn status-btn-maybe ${guest.status === 'pending' ? 'active' : ''}`} onClick={() => updateGuestStatus(eventId, guest.id, 'pending')}>?</button>
                <button className={`status-btn status-btn-no ${guest.status === 'declined' ? 'active' : ''}`} onClick={() => updateGuestStatus(eventId, guest.id, 'declined')}>nej</button>
                {canDelete(eventId) && <button className="status-btn" style={{ color: '#DF83A9', background: 'none', border: 'none', fontSize: 12, cursor: 'pointer', padding: '4px 6px' }} onClick={() => deleteGuest(eventId, guest.id)}>slet</button>}
              </div>
            </div>
          ))}
          {filtered.length === 0 && <p style={{ fontSize: 13, color: '#DF83A9', textAlign: 'center', padding: '32px 0' }}>ingen gæster her</p>}
        </div>
      </div>

      {addOpen && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setAddOpen(false)}>
          <div className="modal">
            <div className="modal-header">
              <div><div className="modal-title">tilføj gæst</div></div>
              <button className="modal-close" onClick={() => setAddOpen(false)}>×</button>
            </div>
            <form onSubmit={handleAdd}>
              <div className="modal-body">
                <div className="input-group"><label className="input-label">navn</label><input required className="input" placeholder="fuldt navn" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} /></div>
                <div className="form-grid-2">
                  <div className="input-group"><label className="input-label">email</label><input className="input" type="email" placeholder="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} /></div>
                  <div className="input-group"><label className="input-label">telefon</label><input className="input" placeholder="tlf." value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} /></div>
                </div>
                <div className="input-group"><label className="input-label">diæt / allergi</label><input className="input" placeholder="fx vegetar, nødde-allergi..." value={form.dietary} onChange={e => setForm({ ...form, dietary: e.target.value })} /></div>
                <div className="input-group"><label className="input-label">noter</label><textarea className="input" rows={2} style={{ resize: 'none' }} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} /></div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }} onClick={() => setForm({ ...form, plusOne: !form.plusOne })}>
                  <div className={`checkbox ${form.plusOne ? 'checked' : ''}`}>
                    {form.plusOne && <svg width="9" height="7" viewBox="0 0 9 7" fill="none"><path d="M1 3.5l2.5 2.5 4.5-5" stroke="white" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                  </div>
                  <span style={{ fontSize: 12, fontWeight: 500 }}>kommer med ledsager</span>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="modal-cancel" onClick={() => setAddOpen(false)}>annuller</button>
                <button type="submit" className="modal-confirm">tilføj gæst</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </AppLayout>
  )
}

// Shopping.js
export function Shopping() {
  const { eventId } = useParams()
  const { events, shopping, updateShoppingQty, toggleShoppingPurchased, addShoppingItem, deleteShoppingItem, canDelete, cleanupOrphanShoppingItems } = useApp()
  const [addOpen, setAddOpen] = useState(false)
  const [editingItem, setEditingItem] = useState(null)
  const [form, setForm] = useState({ name: '', link: '', price: '', quantity: '1', category: 'Kolonial' })

  const event = events && events.find(e => e.id === eventId)
  const items = shopping[eventId] || []
  const total = items.reduce((s, i) => s + (i.price || 0) * i.quantity, 0)
  const remaining = items.filter(i => !i.purchased).reduce((s, i) => s + (i.price || 0) * i.quantity, 0)

  return (
    <AppLayout>
      <div className="page-header">
        <div className="page-header-row1">
          <div className="page-header-left">
            <h1 className="page-title">indkøbsliste</h1>
            <span className="page-subtitle">{event?.name}</span>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn-primary" onClick={() => setAddOpen(true)}>
              <svg width="11" height="11" viewBox="0 0 11 11" fill="none"><path d="M5.5 1v9M1 5.5h9" stroke="white" strokeWidth="1.5" strokeLinecap="round"/></svg>
              tilføj vare
            </button>
            <button style={{ fontSize: 12, fontWeight: 600, color: '#6c63ff', background: '#f7f5ff', border: '1px solid #e0d5ff', borderRadius: 6, padding: '8px 12px', cursor: 'pointer', fontFamily: 'inherit' }} onClick={() => cleanupOrphanShoppingItems(eventId)}>
               ryd op
            </button>
          </div>
        </div>
      </div>

      <div className="page-content">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <div className="stat-card" style={{ background: '#fff' }}><div className="stat-label" style={{ color: '#DF83A9' }}>samlet</div><div className="stat-value">{total.toLocaleString('da-DK')} kr</div><div className="stat-sub" style={{ color: '#DF83A9' }}>{items.length} varer</div></div>
          <div className="stat-card" style={{ background: '#fff3e0' }}><div className="stat-label" style={{ color: '#9a5800' }}>mangler stadig</div><div className="stat-value">{remaining.toLocaleString('da-DK')} kr</div><div className="stat-sub" style={{ color: '#9a5800' }}>{items.filter(i => !i.purchased).length} varer</div></div>
        </div>

        <div>
          {(() => {
            const categoryMap = {
              'Mad': ['Kolonial', 'Frugt & Grønt', 'Mejeri produkter', 'Kød & Fisk', 'Frost', 'Brød', 'Slik & Snacks', 'Mad ud af huset'],
              'Drikkevarer': ['Drikkevarer'],
              'Dekoration': ['Dekoration'],
              'Stole & Borde': ['Stole & Borde'],
              'Invitationer': ['Invitationer'],
              'Musik & Underholdning': ['Musik & Underholdning'],
              'Transport': ['Transport'],
              'Blomster': ['Blomster']
            }

            const grouped = items.reduce((acc, item) => {
              const cat = item.category || 'Øvrigt'
              if (!acc[cat]) acc[cat] = []
              acc[cat].push(item)
              return acc
            }, {})

            // Group by parent category
            const parentGrouped = {}
            Object.entries(categoryMap).forEach(([parent, subs]) => {
              parentGrouped[parent] = {}
              subs.forEach(sub => {
                if (grouped[sub]) {
                  parentGrouped[parent][sub] = grouped[sub]
                }
              })
            })

            const parentOrder = ['Mad', 'Drikkevarer', 'Dekoration', 'Stole & Borde', 'Invitationer', 'Musik & Underholdning', 'Transport', 'Blomster']
            const activeParents = parentOrder.filter(p => Object.keys(parentGrouped[p]).length > 0)

            return activeParents.length > 0 ? activeParents.map(parentCat => (
              <div key={parentCat} style={{ marginBottom: 24 }}>
                <div style={{ fontSize: 15, fontWeight: 700, color: '#222222', marginBottom: 12 }}>
                  {parentCat}
                </div>
                {Object.entries(parentGrouped[parentCat]).map(([subCat, subItems]) => (
                  <div key={subCat} style={{ marginBottom: 14 }}>
                    {parentCat !== 'Drikkevarer' && parentCat !== 'Dekoration' && parentCat !== 'Stole & Borde' && parentCat !== 'Invitationer' && parentCat !== 'Musik & Underholdning' && parentCat !== 'Transport' && parentCat !== 'Blomster' && (
                      <div style={{ fontSize: 12, fontWeight: 600, color: '#6c63ff', marginBottom: 8, paddingLeft: 8 }}>
                        {subCat}
                      </div>
                    )}
                    {subItems.map(item => (
                  <div key={item.id} className={`shopping-item ${item.purchased ? 'purchased' : ''}`}>
                    <div
                      className={`checkbox ${item.purchased ? 'checked' : ''}`}
                      style={{ flexShrink: 0 }}
                      onClick={() => toggleShoppingPurchased(eventId, item.id)}
                    >
                      {item.purchased && <svg width="9" height="7" viewBox="0 0 9 7" fill="none"><path d="M1 3.5l2.5 2.5 4.5-5" stroke="white" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p className={`shopping-name ${item.purchased ? 'purchased' : ''}`}>{item.name}</p>
                      <div style={{ display: 'flex', gap: 8, marginTop: 3, flexWrap: 'wrap' }}>
                        {item.price && <span style={{ fontSize: 11, fontWeight: 600, color: '#6c63ff' }}>{item.price} kr/stk</span>}
                        {item.link && <a href={item.link} target="_blank" rel="noreferrer" className="shopping-link">{item.link.replace('https://', '')}</a>}
                      </div>
                    </div>
                    <div style={{ fontSize: 11, color: '#DF83A9', minWidth: 40, textAlign: 'right' }}>antal: {item.quantity}</div>
                    {canDelete(eventId) && (
                      <>
                        <button className="btn-delete" onClick={() => { setEditingItem(item); setForm({ name: item.name, link: item.link || '', price: item.price?.toString() || '', quantity: item.quantity?.toString() || '1', category: item.category }); setAddOpen(true) }} style={{ padding: '4px 8px', fontSize: 11, color: '#6c63ff', background: 'none', border: 'none', cursor: 'pointer' }}>rediger</button>
                        <button className="btn-delete" onClick={() => deleteShoppingItem(eventId, item.id)} style={{ padding: '4px 8px', fontSize: 11, color: '#DF83A9', background: 'none', border: 'none', cursor: 'pointer' }}>slet</button>
                      </>
                    )}
                  </div>
                ))}
                  </div>
                ))}
              </div>
            )) : (
              <div style={{ textAlign: 'center', padding: '40px 0' }}>
                <p style={{ fontSize: 13, color: '#DF83A9' }}>ingen varer endnu</p>
                <button className="btn-primary" style={{ margin: '12px auto 0', display: 'inline-flex' }} onClick={() => setAddOpen(true)}>tilføj første vare</button>
              </div>
            )
          })()}
        </div>
      </div>

      {addOpen && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setAddOpen(false)}>
          <div className="modal">
            <div className="modal-header">
              <div><div className="modal-title">{editingItem ? 'rediger vare' : 'tilføj vare'}</div></div>
              <button className="modal-close" onClick={() => { setAddOpen(false); setEditingItem(null); setForm({ name: '', link: '', price: '', quantity: '1', category: 'Kolonial' }) }}>×</button>
            </div>
            <form onSubmit={async (e) => {
              e.preventDefault()
              if (form.name) {
                if (editingItem) {
                  await updateShoppingQty(eventId, editingItem.id, parseInt(form.quantity) || 1)
                  const fs = await import('../services/firestore')
                  const updates = {
                    name: form.name,
                    category: form.category || 'Kolonial'
                  }
                  if (form.link) updates.link = form.link
                  if (form.price) updates.price = parseFloat(form.price)
                  await fs.updateShoppingItemFirestore(eventId, editingItem.id, updates)
                } else {
                  await addShoppingItem(eventId, {
                    name: form.name,
                    link: form.link || undefined,
                    price: form.price ? parseFloat(form.price) : undefined,
                    quantity: parseInt(form.quantity) || 1,
                    category: form.category || 'Kolonial'
                  })
                }
                setAddOpen(false)
                setEditingItem(null)
                setForm({ name: '', link: '', price: '', quantity: '1', category: 'Kolonial' })
              }
            }}>
              <div className="modal-body">
                <div className="input-group"><label className="input-label">navn på vare</label><input required className="input" placeholder="fx blomsterkrans..." value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} /></div>
                <div className="input-group"><label className="input-label">link</label><input className="input" placeholder="https://..." value={form.link} onChange={e => setForm({ ...form, link: e.target.value })} /></div>
                <div className="form-grid-2">
                  <div className="input-group"><label className="input-label">pris pr. stk.</label><input className="input" type="number" placeholder="0 kr" value={form.price} onChange={e => setForm({ ...form, price: e.target.value })} /></div>
                  <div className="input-group"><label className="input-label">antal</label><input className="input" type="number" min="1" value={form.quantity} onChange={e => setForm({ ...form, quantity: e.target.value })} /></div>
                </div>
                <div className="input-group"><label className="input-label">kategori</label><select className="input" value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}>
                  <optgroup label="Mad">
                    <option value="Kolonial">Kolonial</option>
                    <option value="Frugt & Grønt">Frugt & Grønt</option>
                    <option value="Mejeri produkter">Mejeri produkter</option>
                    <option value="Kød & Fisk">Kød & Fisk</option>
                    <option value="Frost">Frost</option>
                    <option value="Brød">Brød</option>
                  </optgroup>
                  <option value="Drikkevarer">Drikkevarer</option>
                  <option value="Dekoration">Dekoration</option>
                  <option value="Blomster">Blomster</option>
                  <option value="Stole & Borde">Stole & Borde</option>
                  <option value="Invitationer">Invitationer</option>
                  <option value="Musik & Underholdning">Musik & Underholdning</option>
                  <option value="Transport">Transport</option>
                </select></div>
              </div>
              <div className="modal-footer">
                <button type="button" className="modal-cancel" onClick={() => { setAddOpen(false); setEditingItem(null); setForm({ name: '', link: '', price: '', quantity: '1', category: 'Kolonial' }) }}>annuller</button>
                <button type="submit" className="modal-confirm">{editingItem ? 'gem ændringer' : 'tilføj vare'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </AppLayout>
  )
}

// Madplan.js
export function Madplan() {
  const { eventId } = useParams()
  const navigate = useNavigate()
  const { events, meals, toggleMealSelected, toggleMealShoppingItem, updateMeal, addMeal, deleteMeal, canDelete, currentUser, catering, addCatering, updateCatering, deleteCatering, restaurants, addRestaurant, updateRestaurant, deleteRestaurant, cleanupDuplicateMeals } = useApp()
  const [activeTab, setActiveTab] = useState('menu')
  const [filter, setFilter] = useState('alle')
  const [selectedMeal, setSelectedMeal] = useState(null)
  const [isEditingMeal, setIsEditingMeal] = useState(false)
  const [addMealOpen, setAddMealOpen] = useState(false)
  const [newMealForm, setNewMealForm] = useState({ name: '', category: 'Forret', description: '', portions: 8, price: 200, assignedTo: '', link: '', image: '', shoppingList: [], ingredients: [], steps: [] })
  const [newShoppingItem, setNewShoppingItem] = useState('')
  const [newShoppingCategory, setNewShoppingCategory] = useState('Kolonial')
  const [newShoppingPrice, setNewShoppingPrice] = useState('')
  const [newShoppingQty, setNewShoppingQty] = useState('1')
  const [newIngredient, setNewIngredient] = useState('')
  const [newStep, setNewStep] = useState('')
  const [newImageUploading, setNewImageUploading] = useState(false)
  const [editMealForm, setEditMealForm] = useState(null)
  const [editShoppingItem, setEditShoppingItem] = useState('')
  const [editShoppingCategory, setEditShoppingCategory] = useState('Kolonial')
  const [editShoppingPrice, setEditShoppingPrice] = useState('')
  const [editShoppingQty, setEditShoppingQty] = useState('1')
  const [editIngredient, setEditIngredient] = useState('')
  const [editStep, setEditStep] = useState('')
  const [editImageUploading, setEditImageUploading] = useState(false)
  const [showCateringForm, setShowCateringForm] = useState(false)
  const [editingCatering, setEditingCatering] = useState(null)
  const [cateringForm, setCateringForm] = useState({ firma: '', link: '', telefon: '', pris: 0, retter: [] })
  const [newRetNavn, setNewRetNavn] = useState('')
  const [showRestaurantForm, setShowRestaurantForm] = useState(false)
  const [editingRestaurant, setEditingRestaurant] = useState(null)
  const [restaurantForm, setRestaurantForm] = useState({ navn: '', dato: '', tidspunkt: '', adresse: '', antalPersoner: 0, pris: 0, telefon: '', link: '', bekræftet: false })

  const shoppingCategories = ['Kolonial', 'Frugt & Grønt', 'Mejeri produkter', 'Kød & Fisk', 'Frost', 'Brød', 'Drikkevarer', 'Slik & Snacks', 'Mad ud af huset']

  const calculateMealPrice = (shoppingList) => {
    if (!shoppingList || shoppingList.length === 0) return 0
    return shoppingList.reduce((total, item) => {
      const itemPrice = (item.price || 0) * (item.quantity || 1)
      return total + itemPrice
    }, 0)
  }

  const handleImageUpload = async (file, isEditForm = false) => {
    if (!file) return
    try {
      if (isEditForm) setEditImageUploading(true)
      else setNewImageUploading(true)

      const filename = `${Date.now()}_${file.name}`
      const storageRef = ref(storage, `meals/${eventId}/${filename}`)
      await uploadBytes(storageRef, file)
      const downloadURL = await getDownloadURL(storageRef)

      if (isEditForm) {
        setEditMealForm({ ...editMealForm, image: downloadURL })
      } else {
        setNewMealForm({ ...newMealForm, image: downloadURL })
      }
    } catch (error) {
      console.error('Error uploading image:', error)
    } finally {
      if (isEditForm) setEditImageUploading(false)
      else setNewImageUploading(false)
    }
  }

  const event = events && events.find(e => e.id === eventId)
  const eventMeals = meals[eventId] || []

  const categories = ['alle', ...new Set(eventMeals.map(m => m.category))]
  const filtered = filter === 'alle' ? eventMeals : eventMeals.filter(m => m.category === filter)

  return (
    <AppLayout>
      <div className="page-header">
        <div className="page-header-row1">
          <div className="page-header-left">
            <h1 className="page-title">{event?.name}</h1>
            <span className="page-subtitle">Mad & Drikke</span>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            {activeTab === 'menu' && (
              <button className="btn-primary" onClick={() => setAddMealOpen(true)}>
                <svg width="11" height="11" viewBox="0 0 11 11" fill="none"><path d="M5.5 1v9M1 5.5h9" stroke="white" strokeWidth="1.5" strokeLinecap="round"/></svg>
                tilføj ret
              </button>
            )}
            {activeTab === 'drinks' && (
              <>
                <button className="btn-primary" onClick={() => { setNewMealForm({ name: '', category: 'Drink', description: '', portions: 8, price: 200, assignedTo: '', link: '', image: '', shoppingList: [], ingredients: [], steps: [] }); setAddMealOpen(true) }}>
                  <svg width="11" height="11" viewBox="0 0 11 11" fill="none"><path d="M5.5 1v9M1 5.5h9" stroke="white" strokeWidth="1.5" strokeLinecap="round"/></svg>
                  tilføj drink
                </button>
                <button style={{ fontSize: 12, fontWeight: 600, color: '#6c63ff', background: '#f7f5ff', border: '1px solid #e0d5ff', borderRadius: 6, padding: '8px 12px', cursor: 'pointer', fontFamily: 'inherit' }} onClick={() => cleanupDuplicateMeals(eventId)}>
                  ryd op
                </button>
              </>
            )}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 12, borderBottom: '1px solid #e0d5ff', paddingBottom: 12 }}>
          {['menu', 'catering', 'restaurant', 'drinks'].map(tab => (
            <button
              key={tab}
              onClick={() => { setActiveTab(tab); setFilter('alle'); }}
              style={{
                fontSize: 14,
                fontWeight: activeTab === tab ? 700 : 600,
                color: activeTab === tab ? event?.color : '#DF83A9',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                paddingBottom: 8,
                borderBottom: activeTab === tab ? `2px solid ${event?.color}` : 'none',
                fontFamily: 'inherit',
                textTransform: 'capitalize'
              }}
            >
              {tab}
            </button>
          ))}
        </div>
      </div>

      <div className="page-content">
        {activeTab === 'menu' && ((() => {
          const CATEGORY_ORDER = ['Forret', 'Hovedret', 'Dessert', 'Snack']
          const grouped = eventMeals.reduce((acc, meal) => {
            if (!acc[meal.category]) acc[meal.category] = []
            acc[meal.category].push(meal)
            return acc
          }, {})
          return CATEGORY_ORDER.filter(cat => grouped[cat]?.length > 0).map(category => (
            <div key={category} style={{ marginBottom: 24 }}>
              <div style={{ fontSize: 18, fontWeight: 700, color: '#222222', marginBottom: 12 }}>
                {category}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 14 }}>
                {grouped[category].map(meal => (
                  <div
                    key={meal.id}
                    className="card"
                    style={{ cursor: 'pointer', transition: 'opacity 0.15s' }}
                    onClick={() => { setIsEditingMeal(false); setEditMealForm(null); setSelectedMeal(meal) }}
                  >
                    {/* Checkbox row */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: '#222222' }}>Ansvarlig: {meal.assignedTo}</div>
                      <div
                        className={`checkbox ${meal.selected ? 'checked' : ''}`}
                        onClick={e => { e.stopPropagation(); toggleMealSelected(eventId, meal.id) }}
                      >
                        {meal.selected && <svg width="9" height="7" viewBox="0 0 9 7" fill="none"><path d="M1 3.5l2.5 2.5 4.5-5" stroke="white" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                      </div>
                    </div>

                    {/* Image area */}
                    <div style={{ width: '100%', height: 150, borderRadius: 12, background: '#f7f5ff', marginBottom: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, color: '#DF83A9' }}>
                      {meal.image ? <img src={meal.image} alt={meal.name} style={{ width: '100%', height: '100%', borderRadius: 12, objectFit: 'cover' }} /> : 'ðŸ½ï¸'}
                    </div>

                    {/* Title */}
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#1a1a2e', marginBottom: 4 }}>{meal.name}</div>

                    {/* Description */}
                    <div style={{ fontSize: 11, color: '#DF83A9', marginBottom: 8, lineHeight: 1.3 }}>{meal.description}</div>

                    {/* Portions and price */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#DF83A9' }}>
                      <span>{meal.portions} port.</span>
                      <span style={{ fontWeight: 600, color: '#1a1a2e' }}>{meal.price} kr</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))
        })())}

        {activeTab === 'menu' && eventMeals.length === 0 && (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <p style={{ fontSize: 13, color: '#DF83A9' }}>ingen retter tilføjet endnu</p>
          </div>
        )}

        {/* CATERING SECTION */}
        {activeTab === 'catering' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
              <h2 style={{ fontSize: 18, fontWeight: 700, color: '#1a1a2e' }}>Catering</h2>
              <button className="btn-primary" onClick={() => { setEditingCatering(null); setCateringForm({ firma: '', link: '', telefon: '', pris: 0, retter: [] }); setShowCateringForm(true); }}>
                <svg width="11" height="11" viewBox="0 0 11 11" fill="none"><path d="M5.5 1v9M1 5.5h9" stroke="white" strokeWidth="1.5" strokeLinecap="round"/></svg>
                Tilføj catering
              </button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16 }}>
              {(catering[eventId] || []).map(cat => (
                <div key={cat.id} className="card" style={{ padding: 16 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: 12 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: '#1a1a2e', marginBottom: 2 }}>{cat.firma}</div>
                      <div style={{ fontSize: 11, color: '#DF83A9', marginBottom: 8 }}>
                        {cat.telefon && <div>ðŸ“ž {cat.telefon}</div>}
                        {cat.link && <a href={cat.link} target="_blank" rel="noreferrer" style={{ color: '#6c63ff', textDecoration: 'none' }}>ðŸ”— Besøg hjemmeside</a>}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <button onClick={() => { setEditingCatering(cat); setCateringForm(cat); setShowCateringForm(true); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#DF83A9', fontSize: 14 }}></button>
                      {canDelete(eventId) && <button onClick={() => deleteCatering(eventId, cat.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#DF83A9', fontSize: 14 }}>×</button>}
                    </div>
                  </div>
                  <div style={{ background: '#f7f5ff', borderRadius: 8, padding: 10, marginBottom: 12 }}>
                    <div style={{ fontSize: 10, color: '#DF83A9', marginBottom: 4 }}>Pris</div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: '#1a1a2e' }}>{cat.pris} kr</div>
                  </div>
                  {cat.retter && cat.retter.length > 0 && (
                    <div>
                      <div style={{ fontSize: 10, fontWeight: 600, color: '#DF83A9', marginBottom: 6 }}>Retter ({cat.retter.length})</div>
                      <ul style={{ fontSize: 11, color: '#1a1a2e', paddingLeft: 16, margin: 0 }}>
                        {cat.retter.map((ret, idx) => <li key={idx} style={{ marginBottom: 2 }}>{ret.navn}</li>)}
                      </ul>
                    </div>
                  )}
                </div>
              ))}
            </div>
            {(!catering[eventId] || catering[eventId].length === 0) && (
              <div style={{ textAlign: 'center', padding: '40px 0' }}>
                <p style={{ fontSize: 13, color: '#DF83A9' }}>ingen catering tilføjet endnu</p>
              </div>
            )}
          </div>
        )}

        {/* RESTAURANT SECTION */}
        {activeTab === 'restaurant' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
              <h2 style={{ fontSize: 18, fontWeight: 700, color: '#1a1a2e' }}>Restaurant</h2>
              <button className="btn-primary" onClick={() => { setEditingRestaurant(null); setRestaurantForm({ navn: '', dato: '', tidspunkt: '', adresse: '', antalPersoner: 0, pris: 0, telefon: '', link: '', bekræftet: false }); setShowRestaurantForm(true); }}>
                <svg width="11" height="11" viewBox="0 0 11 11" fill="none"><path d="M5.5 1v9M1 5.5h9" stroke="white" strokeWidth="1.5" strokeLinecap="round"/></svg>
                tilføj restaurant
              </button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16 }}>
              {(restaurants[eventId] || []).map(rest => (
                <div key={rest.id} className="card" style={{ padding: 16 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: 12 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: '#1a1a2e', marginBottom: 4 }}>{rest.navn}</div>
                      <div style={{ fontSize: 11, color: '#DF83A9', marginBottom: 8, lineHeight: 1.4 }}>
                        <div> {rest.dato} {rest.tidspunkt && `kl. ${rest.tidspunkt}`}</div>
                        {rest.adresse && <div> {rest.adresse}</div>}
                        {rest.telefon && <div> {rest.telefon}</div>}
                        {rest.link && <a href={rest.link} target="_blank" rel="noreferrer" style={{ color: '#6c63ff', textDecoration: 'none' }}> Hjemmeside</a>}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <button onClick={() => { setEditingRestaurant(rest); setRestaurantForm(rest); setShowRestaurantForm(true); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#DF83A9', fontSize: 14 }}>âœï¸</button>
                      {canDelete(eventId) && <button onClick={() => deleteRestaurant(eventId, rest.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#DF83A9', fontSize: 14 }}>×</button>}
                    </div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
                    <div style={{ background: '#f7f5ff', borderRadius: 8, padding: 10 }}>
                      <div style={{ fontSize: 10, color: '#DF83A9' }}>Antal personer</div>
                      <div style={{ fontSize: 14, fontWeight: 600, color: '#1a1a2e' }}>{rest.antalPersoner}</div>
                    </div>
                    <div style={{ background: '#f7f5ff', borderRadius: 8, padding: 10 }}>
                      <div style={{ fontSize: 10, color: '#DF83A9' }}>Pris</div>
                      <div style={{ fontSize: 14, fontWeight: 600, color: '#1a1a2e' }}>{rest.pris} kr</div>
                    </div>
                  </div>
                  <div style={{ padding: 8, borderRadius: 6, background: rest.bekræftet ? '#e8fdf5' : '#fff3e0', textAlign: 'center' }}>
                    <span style={{ fontSize: 11, fontWeight: 600, color: rest.bekræftet ? '#007a58' : '#9a5800' }}>
                      {rest.bekræftet ? 'bekræftet' : 'Afventer'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
            {(!restaurants[eventId] || restaurants[eventId].length === 0) && (
              <div style={{ textAlign: 'center', padding: '40px 0' }}>
                <p style={{ fontSize: 13, color: '#DF83A9' }}>Ingen restaurantbooking tilføjet endnu</p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'drinks' && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 14 }}>
            {eventMeals.filter(m => m.category === 'Drink').map(meal => (
              <div
                key={meal.id}
                className="card"
                style={{ cursor: 'pointer', transition: 'opacity 0.15s' }}
                onClick={() => setSelectedMeal(meal)}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#222222' }}>Ansvarlig: {meal.assignedTo}</div>
                  <div
                    className={`checkbox ${meal.selected ? 'checked' : ''}`}
                    onClick={e => { e.stopPropagation(); toggleMealSelected(eventId, meal.id) }}
                  >
                    {meal.selected && <svg width="9" height="7" viewBox="0 0 9 7" fill="none"><path d="M1 3.5l2.5 2.5 4.5-5" stroke="white" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                  </div>
                </div>
                <div style={{ width: '100%', height: 150, borderRadius: 12, background: '#f7f5ff', marginBottom: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, color: '#DF83A9' }}>
                  {meal.image ? <img src={meal.image} alt={meal.name} style={{ width: '100%', height: '100%', borderRadius: 12, objectFit: 'cover' }} /> : 'ðŸ¥¤'}
                </div>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#1a1a2e', marginBottom: 4 }}>{meal.name}</div>
                <div style={{ fontSize: 11, color: '#DF83A9', marginBottom: 8, lineHeight: 1.3 }}>{meal.description}</div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#DF83A9' }}>
                  <span>{meal.portions} port.</span>
                  <span style={{ fontWeight: 600, color: '#1a1a2e' }}>{meal.price} kr</span>
                </div>
              </div>
            ))}
          </div>
        )}

        {activeTab === 'drinks' && eventMeals.filter(m => m.category === 'Drink').length === 0 && (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <p style={{ fontSize: 13, color: '#DF83A9' }}>ingen drinks tilføjet endnu</p>
          </div>
        )}
      </div>

      {selectedMeal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setSelectedMeal(null)}>
          <div className="modal">
            <div className="modal-header">
              <div>
                <div className="modal-title">{selectedMeal.name}</div>
                <div className="modal-subtitle">{selectedMeal.category}</div>
              </div>
              <button className="modal-close" onClick={() => setSelectedMeal(null)}>×</button>
            </div>
            {!isEditingMeal && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, padding: '16px 16px 16px 16px', background: '#fff', position: 'sticky', top: 0, zIndex: 10, marginTop: 16 }}>
                <div style={{ background: '#f7f5ff', borderRadius: 12, padding: 10, textAlign: 'center' }}>
                  <div style={{ fontSize: 10, color: '#DF83A9' }}>portioner</div>
                  <div style={{ fontSize: 20, fontWeight: 700, color: '#1a1a2e' }}>{selectedMeal.portions}</div>
                </div>
                <div style={{ background: '#f7f5ff', borderRadius: 12, padding: 10, textAlign: 'center' }}>
                  <div style={{ fontSize: 10, color: '#DF83A9' }}>pris i alt</div>
                  <div style={{ fontSize: 20, fontWeight: 700, color: '#1a1a2e' }}>{selectedMeal.price} kr</div>
                </div>
                <div style={{ background: '#f7f5ff', borderRadius: 12, padding: 10, textAlign: 'center' }}>
                  <div style={{ fontSize: 10, color: '#DF83A9' }}>ansvarlig</div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#1a1a2e', marginTop: 4 }}>{selectedMeal.assignedTo}</div>
                </div>
              </div>
            )}
            <div className="modal-body">
              {!isEditingMeal ? (
                <>
                  {/* Image */}
                  {selectedMeal.image && (
                    <div style={{ marginBottom: 16 }}>
                      <img src={selectedMeal.image} alt={selectedMeal.name} style={{ width: '100%', borderRadius: 12 }} />
                    </div>
                  )}

                  {/* Opskrift */}
                  {selectedMeal.steps && selectedMeal.steps.length > 0 && (
                    <div style={{ marginBottom: 18 }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: '#1a1a2e', marginBottom: 8 }}>Opskrift</div>
                      <ol style={{ paddingLeft: 20, color: '#1a1a2e', fontSize: 12, marginBottom: 10 }}>
                        {selectedMeal.steps.map((step, i) => <li key={i} style={{ marginBottom: 6 }}>{typeof step === 'object' ? step.text : step}</li>)}
                      </ol>
                      {selectedMeal.link && (
                        <a href={selectedMeal.link} target="_blank" rel="noreferrer" className="shopping-link" style={{ fontSize: 12 }}>
                          Se original opskrift 
                        </a>
                      )}
                    </div>
                  )}

                  {/* Ingredienser */}
                  {selectedMeal.ingredients && selectedMeal.ingredients.length > 0 && (
                    <div style={{ marginBottom: 18 }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: '#1a1a2e', marginBottom: 8 }}>Ingredienser</div>
                      <ul style={{ paddingLeft: 20, color: '#1a1a2e', fontSize: 12 }}>
                        {selectedMeal.ingredients.map((ing, i) => <li key={i} style={{ marginBottom: 4 }}>{typeof ing === 'object' ? ing.text : ing}</li>)}
                      </ul>
                    </div>
                  )}

                  {/* indkøbsliste */}
                  <div style={{ marginBottom: 18 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: '#1a1a2e', marginBottom: 8 }}>indkøbsliste</div>
                    {selectedMeal.shoppingList && selectedMeal.shoppingList.map(item => (
                      <div key={item.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid #f0eeff' }}>
                        <div
                          className={`checkbox ${item.checked ? 'checked' : ''}`}
                          style={{ width: 18, height: 18 }}
                          onClick={() => toggleMealShoppingItem(eventId, selectedMeal.id, item.id)}
                        >
                          {item.checked && <svg width="9" height="7" viewBox="0 0 9 7" fill="none"><path d="M1 3.5l2.5 2.5 4.5-5" stroke="white" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                        </div>
                        <span style={{ fontSize: 12, color: '#1a1a2e', flex: 1 }}>{item.name}</span>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <>
                  <div className="input-group"><label className="input-label">navn på ret</label><input className="input" placeholder="fx Laksesteak" value={editMealForm.name} onChange={e => setEditMealForm({ ...editMealForm, name: e.target.value })} /></div>
                  <div className="input-group"><label className="input-label">kategori</label><select className="input" style={{ appearance: 'none', cursor: 'pointer' }} value={editMealForm.category} onChange={e => setEditMealForm({ ...editMealForm, category: e.target.value })}>
                    <option>Forret</option>
                    <option>Hovedret</option>
                    <option>Dessert</option>
                    <option>Snack</option>
                  </select></div>
                  <div className="input-group"><label className="input-label">Link til opskrift</label><input className="input" placeholder="https://..." value={editMealForm.link} onChange={e => setEditMealForm({ ...editMealForm, link: e.target.value })} /></div>
                  <div className="input-group">
                    <label className="input-label">billede</label>
                    <input className="input" placeholder="https://..." value={editMealForm.image} onChange={e => setEditMealForm({ ...editMealForm, image: e.target.value })} style={{ marginBottom: 8 }} />
                    <div style={{ fontSize: 11, color: '#DF83A9', marginBottom: 8 }}>eller upload billede:</div>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={e => e.target.files?.[0] && handleImageUpload(e.target.files[0], true)}
                      disabled={editImageUploading}
                      className="input"
                      style={{ cursor: editImageUploading ? 'not-allowed' : 'pointer' }}
                    />
                    {editImageUploading && <div style={{ fontSize: 11, color: '#6c63ff', marginTop: 8 }}>uploader...</div>}
                    {editMealForm.image && (
                      <div style={{ marginTop: 12, borderRadius: 12, overflow: 'hidden', maxHeight: 150 }}>
                        <img src={editMealForm.image} alt="preview" style={{ maxWidth: '100%', maxHeight: 150, objectFit: 'cover' }} />
                      </div>
                    )}
                  </div>
                  <div className="form-grid-2">
                    <div className="input-group"><label className="input-label">portioner</label><input className="input" type="number" value={editMealForm.portions} onChange={e => setEditMealForm({ ...editMealForm, portions: parseInt(e.target.value) })} /></div>
                    <div className="input-group"><label className="input-label">pris i alt (beregnet)</label><input className="input" type="number" value={calculateMealPrice(editMealForm.shoppingList)} disabled style={{ background: '#f7f5ff', cursor: 'not-allowed' }} /></div>
                  </div>
                  <div className="input-group">
                    <label className="input-label">Ingredienser</label>
                    <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                      <input className="input" style={{ flex: 1 }} placeholder={editMealForm.category === 'Drink' ? 'fx vodka, frugtsaft, isterninger...' : 'fx 500g mel, 2 æg...'} value={editIngredient} onChange={e => setEditIngredient(e.target.value)} />
                      <button className="btn-primary" style={{ flexShrink: 0 }} onClick={() => { if (editIngredient.trim()) { setEditMealForm({ ...editMealForm, ingredients: [...(editMealForm.ingredients || []), { id: Date.now().toString(), text: editIngredient }] }); setEditIngredient('') } }}>tilføj</button>
                    </div>
                    {editMealForm.ingredients && editMealForm.ingredients.length > 0 && (
                      <div style={{ background: '#f7f5ff', borderRadius: 12, padding: 10 }}>
                        {editMealForm.ingredients.map(ing => (
                          <div key={ing.id} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, fontSize: 12 }}>
                            <span style={{ flex: 1, color: '#1a1a2e' }}>• {ing.text}</span>
                            <button style={{ padding: 0, background: 'none', border: 'none', color: '#DF83A9', cursor: 'pointer', fontSize: 14 }} onClick={() => setEditMealForm({ ...editMealForm, ingredients: editMealForm.ingredients.filter(i => i.id !== ing.id) })}>×</button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="input-group">
                    <label className="input-label">Opskrift</label>
                    <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                      <input className="input" style={{ flex: 1 }} placeholder={editMealForm.category === 'Drink' ? 'fx blanding, ryst, servér...' : 'fx Pisk æg og sukker, tilføj mel...'} value={editStep} onChange={e => setEditStep(e.target.value)} />
                      <button className="btn-primary" style={{ flexShrink: 0 }} onClick={() => { if (editStep.trim()) { setEditMealForm({ ...editMealForm, steps: [...(editMealForm.steps || []), { id: Date.now().toString(), text: editStep, order: (editMealForm.steps?.length || 0) + 1 }] }); setEditStep('') } }}>tilføj</button>
                    </div>
                    {editMealForm.steps && editMealForm.steps.length > 0 && (
                      <div style={{ background: '#f7f5ff', borderRadius: 12, padding: 10 }}>
                        {editMealForm.steps.map(step => (
                          <div key={step.id} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, fontSize: 12 }}>
                            <span style={{ color: '#6c63ff', fontWeight: 600, minWidth: 24 }}>{step.order}.</span>
                            <span style={{ flex: 1, color: '#1a1a2e' }}>{step.text}</span>
                            <button style={{ padding: 0, background: 'none', border: 'none', color: '#DF83A9', cursor: 'pointer', fontSize: 14 }} onClick={() => setEditMealForm({ ...editMealForm, steps: editMealForm.steps.filter(i => i.id !== step.id).map((s, idx) => ({ ...s, order: idx + 1 })) })}>×</button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="input-group">
                    <label className="input-label">indkøbsliste</label>
                    <select className="input" value={editShoppingCategory} onChange={e => setEditShoppingCategory(e.target.value)} style={{ marginBottom: 8, appearance: 'none', cursor: 'pointer' }}>
                      {shoppingCategories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                    </select>
                    <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                      <input className="input" style={{ flex: 1 }} placeholder="fx 500g laks, smør, salt..." value={editShoppingItem} onChange={e => setEditShoppingItem(e.target.value)} />
                      <input className="input" style={{ width: 70 }} type="number" placeholder="pris" value={editShoppingPrice} onChange={e => setEditShoppingPrice(e.target.value)} />
                      <input className="input" style={{ width: 50 }} type="number" placeholder="qty" min="1" value={editShoppingQty} onChange={e => setEditShoppingQty(e.target.value)} />
                      <button className="btn-primary" style={{ flexShrink: 0 }} onClick={() => { if (editShoppingItem.trim()) { setEditMealForm({ ...editMealForm, shoppingList: [...(editMealForm.shoppingList || []), { id: Date.now().toString(), name: editShoppingItem, category: editShoppingCategory, price: editShoppingPrice ? parseFloat(editShoppingPrice) : undefined, quantity: parseInt(editShoppingQty) || 1, checked: false }] }); setEditShoppingItem(''); setEditShoppingPrice(''); setEditShoppingQty('1') } }}>tilføj</button>
                    </div>
                    {editMealForm.shoppingList && editMealForm.shoppingList.length > 0 && (
                      <div style={{ background: '#f7f5ff', borderRadius: 12, padding: 10 }}>
                        {editMealForm.shoppingList.map(item => (
                          <div key={item.id} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, fontSize: 12 }}>
                            <span style={{ flex: 1, color: '#1a1a2e' }}>{item.name}</span>
                            <span style={{ fontSize: 10, color: '#DF83A9', whiteSpace: 'nowrap' }}>{item.category}</span>
                            {item.price && <span style={{ fontSize: 10, color: '#6c63ff', fontWeight: 600, whiteSpace: 'nowrap' }}>{item.price} kr × {item.quantity}</span>}
                            <button style={{ padding: 0, background: 'none', border: 'none', color: '#DF83A9', cursor: 'pointer', fontSize: 14 }} onClick={() => setEditMealForm({ ...editMealForm, shoppingList: editMealForm.shoppingList.filter(i => i.id !== item.id) })}>×</button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="input-group"><label className="input-label">ansvarlig</label><select className="input" style={{ appearance: 'none', cursor: 'pointer' }} value={editMealForm.assignedTo} onChange={e => setEditMealForm({ ...editMealForm, assignedTo: e.target.value })}>
                    <option value="">– vælg team medlem –</option>
                    <option value={currentUser.name}>{currentUser.name} (dig selv)</option>
                    {event?.members?.filter(m => m.name !== currentUser.name).map(m => <option key={m.id} value={m.name}>{m.name}</option>)}
                  </select></div>
                </>
              )}
            </div>
            <div className="modal-footer">
              {!isEditingMeal ? (
                <>
                  <button className="modal-cancel" onClick={() => setSelectedMeal(null)}>luk</button>
                  {canDelete(eventId) && <button className="modal-cancel" style={{ color: '#6c63ff', borderColor: '#6c63ff' }} onClick={() => { setIsEditingMeal(true); setEditMealForm({ ...selectedMeal, ingredients: selectedMeal.ingredients || [], steps: selectedMeal.steps || [] }); setEditIngredient(''); setEditStep('') }}>rediger</button>}
                  {canDelete(eventId) && <button className="modal-cancel" style={{ color: '#cc2020', borderColor: '#cc2020' }} onClick={() => { deleteMeal(eventId, selectedMeal.id); setSelectedMeal(null) }}>{selectedMeal.category === 'Drink' ? 'slet drink' : 'slet ret'}</button>}
                </>
              ) : (
                <>
                  <button className="modal-cancel" onClick={() => { setIsEditingMeal(false); setEditMealForm(null); setEditShoppingItem(''); setEditIngredient(''); setEditStep('') }}>annuller</button>
                  <button className="modal-confirm" onClick={() => { if (!selectedMeal?.id) { alert('Fejl: Drinken har ingen ID. Prøv at opdatere siden'); return; } updateMeal(eventId, selectedMeal.id, editMealForm); setSelectedMeal({ ...selectedMeal, ...editMealForm }); setIsEditingMeal(false); setEditMealForm(null); setEditShoppingItem(''); setEditIngredient(''); setEditStep('') }}>gem ændringer</button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {addMealOpen && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setAddMealOpen(false)}>
          <div className="modal">
            <div className="modal-header">
              <div><div className="modal-title">{newMealForm.category === 'Drink' ? 'tilføj drink' : 'tilføj ret'}</div></div>
              <button className="modal-close" onClick={() => setAddMealOpen(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="input-group"><label className="input-label">{newMealForm.category === 'Drink' ? 'navn på drink' : 'navn på ret'}</label><input className="input" placeholder={newMealForm.category === 'Drink' ? 'fx Sangria' : 'fx Laksesteak'} value={newMealForm.name} onChange={e => setNewMealForm({ ...newMealForm, name: e.target.value })} /></div>
              {newMealForm.category !== 'Drink' && (
                <div className="input-group"><label className="input-label">kategori</label><select className="input" style={{ appearance: 'none', cursor: 'pointer' }} value={newMealForm.category} onChange={e => setNewMealForm({ ...newMealForm, category: e.target.value })}>
                  <option>Forret</option>
                  <option>Hovedret</option>
                  <option>Dessert</option>
                  <option>Snack</option>
                </select></div>
              )}
              <div className="input-group"><label className="input-label">link til opskrift</label><input className="input" placeholder="https://..." value={newMealForm.link} onChange={e => setNewMealForm({ ...newMealForm, link: e.target.value })} /></div>
              <div className="input-group">
                <label className="input-label">billede</label>
                <input className="input" placeholder="https://..." value={newMealForm.image} onChange={e => setNewMealForm({ ...newMealForm, image: e.target.value })} style={{ marginBottom: 8 }} />
                <div style={{ fontSize: 11, color: '#DF83A9', marginBottom: 8 }}>eller upload billede:</div>
                <input
                  type="file"
                  accept="image/*"
                  onChange={e => e.target.files?.[0] && handleImageUpload(e.target.files[0], false)}
                  disabled={newImageUploading}
                  className="input"
                  style={{ cursor: newImageUploading ? 'not-allowed' : 'pointer' }}
                />
                {newImageUploading && <div style={{ fontSize: 11, color: '#6c63ff', marginTop: 8 }}>uploader...</div>}
                {newMealForm.image && (
                  <div style={{ marginTop: 12, borderRadius: 12, overflow: 'hidden', maxHeight: 150 }}>
                    <img src={newMealForm.image} alt="preview" style={{ maxWidth: '100%', maxHeight: 150, objectFit: 'cover' }} />
                  </div>
                )}
              </div>
              <div className="form-grid-2">
                <div className="input-group"><label className="input-label">portioner</label><input className="input" type="number" value={newMealForm.portions} onChange={e => setNewMealForm({ ...newMealForm, portions: parseInt(e.target.value) })} /></div>
                <div className="input-group"><label className="input-label">pris i alt (beregnet)</label><input className="input" type="number" value={calculateMealPrice(newMealForm.shoppingList)} disabled style={{ background: '#f7f5ff', cursor: 'not-allowed' }} /></div>
              </div>
              <div className="input-group">
                <label className="input-label">Ingredienser</label>
                <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                  <input className="input" style={{ flex: 1 }} placeholder={newMealForm.category === 'Drink' ? 'fx vodka, frugtsaft, isterninger...' : 'fx 500g mel, 2 æg...'} value={newIngredient} onChange={e => setNewIngredient(e.target.value)} />
                  <button className="btn-primary" style={{ flexShrink: 0 }} onClick={() => { if (newIngredient.trim()) { setNewMealForm({ ...newMealForm, ingredients: [...(newMealForm.ingredients || []), { id: Date.now().toString(), text: newIngredient }] }); setNewIngredient('') } }}>tilføj</button>
                </div>
                {newMealForm.ingredients && newMealForm.ingredients.length > 0 && (
                  <div style={{ background: '#f7f5ff', borderRadius: 12, padding: 10 }}>
                    {newMealForm.ingredients.map(ing => (
                      <div key={ing.id} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, fontSize: 12 }}>
                        <span style={{ flex: 1, color: '#1a1a2e' }}>• {ing.text}</span>
                        <button style={{ padding: 0, background: 'none', border: 'none', color: '#DF83A9', cursor: 'pointer', fontSize: 14 }} onClick={() => setNewMealForm({ ...newMealForm, ingredients: newMealForm.ingredients.filter(i => i.id !== ing.id) })}>×</button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="input-group">
                <label className="input-label">Opskrift</label>
                <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                  <input className="input" style={{ flex: 1 }} placeholder={newMealForm.category === 'Drink' ? 'fx blanding, ryst, servér...' : 'fx Pisk æg og sukker, tilføj mel...'} value={newStep} onChange={e => setNewStep(e.target.value)} />
                  <button className="btn-primary" style={{ flexShrink: 0 }} onClick={() => { if (newStep.trim()) { setNewMealForm({ ...newMealForm, steps: [...(newMealForm.steps || []), { id: Date.now().toString(), text: newStep, order: (newMealForm.steps?.length || 0) + 1 }] }); setNewStep('') } }}>tilføj</button>
                </div>
                {newMealForm.steps && newMealForm.steps.length > 0 && (
                  <div style={{ background: '#f7f5ff', borderRadius: 12, padding: 10 }}>
                    {newMealForm.steps.map(step => (
                      <div key={step.id} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, fontSize: 12 }}>
                        <span style={{ color: '#6c63ff', fontWeight: 600, minWidth: 24 }}>{step.order}.</span>
                        <span style={{ flex: 1, color: '#1a1a2e' }}>{step.text}</span>
                        <button style={{ padding: 0, background: 'none', border: 'none', color: '#DF83A9', cursor: 'pointer', fontSize: 14 }} onClick={() => setNewMealForm({ ...newMealForm, steps: newMealForm.steps.filter(i => i.id !== step.id).map((s, idx) => ({ ...s, order: idx + 1 })) })}>×</button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="input-group">
                <label className="input-label">indkøbsliste</label>
                <select className="input" value={newShoppingCategory} onChange={e => setNewShoppingCategory(e.target.value)} style={{ marginBottom: 8, appearance: 'none', cursor: 'pointer' }}>
                  {shoppingCategories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                </select>
                <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                  <input className="input" style={{ flex: 1 }} placeholder="fx 500g laks, smør, salt..." value={newShoppingItem} onChange={e => setNewShoppingItem(e.target.value)} />
                  <input className="input" style={{ width: 70 }} type="number" placeholder="pris" value={newShoppingPrice} onChange={e => setNewShoppingPrice(e.target.value)} />
                  <input className="input" style={{ width: 50 }} type="number" placeholder="qty" min="1" value={newShoppingQty} onChange={e => setNewShoppingQty(e.target.value)} />
                  <button className="btn-primary" style={{ flexShrink: 0 }} onClick={() => { if (newShoppingItem.trim()) { setNewMealForm({ ...newMealForm, shoppingList: [...newMealForm.shoppingList, { id: Date.now().toString(), name: newShoppingItem, category: newShoppingCategory, price: newShoppingPrice ? parseFloat(newShoppingPrice) : undefined, quantity: parseInt(newShoppingQty) || 1, checked: false }] }); setNewShoppingItem(''); setNewShoppingPrice(''); setNewShoppingQty('1') } }}>tilføj</button>
                </div>
                {newMealForm.shoppingList && newMealForm.shoppingList.length > 0 && (
                  <div style={{ background: '#f7f5ff', borderRadius: 12, padding: 10 }}>
                    {newMealForm.shoppingList.map(item => (
                      <div key={item.id} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, fontSize: 12 }}>
                        <span style={{ flex: 1, color: '#1a1a2e' }}>{item.name}</span>
                        <span style={{ fontSize: 10, color: '#DF83A9', whiteSpace: 'nowrap' }}>{item.category}</span>
                        {item.price && <span style={{ fontSize: 10, color: '#6c63ff', fontWeight: 600, whiteSpace: 'nowrap' }}>{item.price} kr × {item.quantity}</span>}
                        <button style={{ padding: 0, background: 'none', border: 'none', color: '#DF83A9', cursor: 'pointer', fontSize: 14 }} onClick={() => setNewMealForm({ ...newMealForm, shoppingList: newMealForm.shoppingList.filter(i => i.id !== item.id) })}>×</button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="input-group"><label className="input-label">ansvarlig</label><select className="input" style={{ appearance: 'none', cursor: 'pointer' }} value={newMealForm.assignedTo} onChange={e => setNewMealForm({ ...newMealForm, assignedTo: e.target.value })}>
                <option value="">– vælg team medlem –</option>
                <option value={currentUser.name}>{currentUser.name} (dig selv)</option>
                {event?.members?.map(m => <option key={m.id} value={m.name}>{m.name}</option>)}
              </select></div>
            </div>
            <div className="modal-footer">
              <button className="modal-cancel" onClick={() => setAddMealOpen(false)}>annuller</button>
              <button className="modal-confirm" onClick={() => { addMeal(eventId, newMealForm); setAddMealOpen(false); setNewMealForm({ name: '', category: 'Forret', description: '', portions: 8, price: 200, assignedTo: '', link: '', image: '', shoppingList: [], ingredients: [], steps: [] }); setNewShoppingItem(''); setNewIngredient(''); setNewStep('') }}>{newMealForm.category === 'Drink' ? 'tilføj drink' : 'tilføj ret'}</button>
            </div>
          </div>
        </div>
      )}

      {/* CATERING MODAL */}
      {showCateringForm && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowCateringForm(false)}>
          <div className="modal" style={{ maxWidth: 500 }}>
            <div className="modal-header">
              <div><div className="modal-title">{editingCatering ? 'Rediger catering' : 'Tilføj catering'}</div></div>
              <button className="modal-close" onClick={() => setShowCateringForm(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="input-group">
                <label className="input-label">Firma navn</label>
                <input className="input" placeholder="fx. Kokken Catering" value={cateringForm.firma} onChange={e => setCateringForm({ ...cateringForm, firma: e.target.value })} />
              </div>
              <div className="input-group">
                <label className="input-label">Telefon</label>
                <input className="input" placeholder="fx. +45 40 40 40 40" value={cateringForm.telefon} onChange={e => setCateringForm({ ...cateringForm, telefon: e.target.value })} />
              </div>
              <div className="input-group">
                <label className="input-label">Link til hjemmeside</label>
                <input className="input" placeholder="https://..." value={cateringForm.link} onChange={e => setCateringForm({ ...cateringForm, link: e.target.value })} />
              </div>
              <div className="input-group">
                <label className="input-label">Pris i alt</label>
                <input className="input" type="number" min="0" value={cateringForm.pris} onChange={e => setCateringForm({ ...cateringForm, pris: parseInt(e.target.value) || 0 })} onFocus={e => e.target.value === '0' && e.target.select()} />
              </div>
              <div className="input-group">
                <label className="input-label">Retter</label>
                <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                  <input className="input" placeholder="fx. Gravlax" value={newRetNavn} onChange={e => setNewRetNavn(e.target.value)} style={{ flex: 1 }} />
                  <button className="btn-primary" onClick={() => { if (newRetNavn) { setCateringForm({ ...cateringForm, retter: [...(cateringForm.retter || []), { id: Date.now(), navn: newRetNavn }] }); setNewRetNavn('') } }}>+</button>
                </div>
                {(cateringForm.retter || []).map((ret, idx) => (
                  <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f7f5ff', padding: 8, borderRadius: 6, marginBottom: 6 }}>
                    <span style={{ fontSize: 12, color: '#1a1a2e' }}>{ret.navn}</span>
                    <button onClick={() => setCateringForm({ ...cateringForm, retter: cateringForm.retter.filter((_, i) => i !== idx) })} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#DF83A9' }}>×</button>
                  </div>
                ))}
              </div>
            </div>
            <div className="modal-footer">
              <button className="modal-cancel" onClick={() => setShowCateringForm(false)}>annuller</button>
              <button className="modal-confirm" onClick={() => {
                if (editingCatering) {
                  updateCatering(eventId, editingCatering.id, cateringForm)
                } else {
                  addCatering(eventId, cateringForm)
                }
                setShowCateringForm(false)
                setCateringForm({ firma: '', link: '', telefon: '', pris: 0, retter: [] })
                setEditingCatering(null)
              }}>{editingCatering ? 'gem ændringer' : 'tilføj catering'}</button>
            </div>
          </div>
        </div>
      )}

      {/* RESTAURANT MODAL */}
      {showRestaurantForm && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowRestaurantForm(false)}>
          <div className="modal" style={{ maxWidth: 500 }}>
            <div className="modal-header">
              <div><div className="modal-title">{editingRestaurant ? 'Rediger restaurant' : 'Tilføj restaurant'}</div></div>
              <button className="modal-close" onClick={() => setShowRestaurantForm(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="input-group">
                <label className="input-label">Restaurant navn</label>
                <input className="input" placeholder="fx. Restaurant Noma" value={restaurantForm.navn} onChange={e => setRestaurantForm({ ...restaurantForm, navn: e.target.value })} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div className="input-group">
                  <label className="input-label">Dato</label>
                  <input className="input" type="date" value={restaurantForm.dato} onChange={e => setRestaurantForm({ ...restaurantForm, dato: e.target.value })} />
                </div>
                <div className="input-group">
                  <label className="input-label">Tidspunkt</label>
                  <input className="input" type="time" value={restaurantForm.tidspunkt} onChange={e => setRestaurantForm({ ...restaurantForm, tidspunkt: e.target.value })} />
                </div>
              </div>
              <div className="input-group">
                <label className="input-label">Adresse</label>
                <input className="input" placeholder="fx. Strandvejen 23, København" value={restaurantForm.adresse} onChange={e => setRestaurantForm({ ...restaurantForm, adresse: e.target.value })} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div className="input-group">
                  <label className="input-label">Antal personer</label>
                  <input className="input" type="number" min="0" value={restaurantForm.antalPersoner} onChange={e => setRestaurantForm({ ...restaurantForm, antalPersoner: parseInt(e.target.value) || 0 })} />
                </div>
                <div className="input-group">
                  <label className="input-label">Pris i alt</label>
                  <input className="input" type="number" min="0" value={restaurantForm.pris} onChange={e => setRestaurantForm({ ...restaurantForm, pris: parseInt(e.target.value) || 0 })} onFocus={e => e.target.value === '0' && e.target.select()} />
                </div>
              </div>
              <div className="input-group">
                <label className="input-label">Telefon</label>
                <input className="input" placeholder="fx. +45 40 40 40 40" value={restaurantForm.telefon} onChange={e => setRestaurantForm({ ...restaurantForm, telefon: e.target.value })} />
              </div>
              <div className="input-group">
                <label className="input-label">Link til hjemmeside</label>
                <input className="input" placeholder="https://..." value={restaurantForm.link} onChange={e => setRestaurantForm({ ...restaurantForm, link: e.target.value })} />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 0' }}>
                <input type="checkbox" checked={restaurantForm.bekræftet} onChange={e => setRestaurantForm({ ...restaurantForm, bekræftet: e.target.checked })} style={{ cursor: 'pointer' }} />
                <label style={{ cursor: 'pointer', fontSize: 13, color: '#1a1a2e' }}>Booking bekræftet</label>
              </div>
            </div>
            <div className="modal-footer">
              <button className="modal-cancel" onClick={() => setShowRestaurantForm(false)}>annuller</button>
              <button className="modal-confirm" onClick={() => {
                if (editingRestaurant) {
                  updateRestaurant(eventId, editingRestaurant.id, restaurantForm)
                } else {
                  addRestaurant(eventId, restaurantForm)
                }
                setShowRestaurantForm(false)
                setRestaurantForm({ navn: '', dato: '', tidspunkt: '', adresse: '', antalPersoner: 0, pris: 0, telefon: '', link: '', bekræftet: false })
                setEditingRestaurant(null)
              }}>{editingRestaurant ? 'gem ændringer' : 'tilføj restaurant'}</button>
            </div>
          </div>
        </div>
      )}

    </AppLayout>
  )
}

export function MusicPage() {
  const { eventId } = useParams()
  const { events, musik, addMusik, updateMusik, deleteMusik, canDelete, currentUser } = useApp()
  const [editingId, setEditingId] = useState(null)
  const [editingValues, setEditingValues] = useState({})
  const [showForm, setShowForm] = useState(false)
  const [formData, setFormData] = useState({
    navn: '',
    type: 'DJ',
    beskrivelse: '',
    pris: 0,
    telefon: '',
    link: '',
    kontakt: '',
    tidspunkt: '',
    bekræftet: false,
    assignedTo: ''
  })

  const event = events?.find(e => e.id === eventId)
  const musikList = musik[eventId] || []
  const musikTypes = ['DJ', 'Band', 'Underholder', 'Andet']

  const handleAddMusik = async () => {
    if (!formData.navn) return
    try {
      await addMusik(eventId, formData)
      setFormData({
        navn: '',
        type: 'DJ',
        beskrivelse: '',
        pris: 0,
        telefon: '',
        link: '',
        kontakt: '',
        tidspunkt: '',
        bekræftet: false,
        assignedTo: ''
      })
      setShowForm(false)
    } catch (error) {
      console.error('Error:', error)
    }
  }

  const handleUpdate = async (id) => {
    try {
      await updateMusik(eventId, id, editingValues[id])
      setEditingId(null)
      setEditingValues({})
    } catch (error) {
      console.error('Error:', error)
    }
  }

  const handleDelete = async (id) => {
    try {
      await deleteMusik(eventId, id)
    } catch (error) {
      console.error('Error:', error)
    }
  }

  return (
    <AppLayout>
      <div className="page-header">
        <div className="page-header-row1">
          <div className="page-header-left">
            <h1 className="page-title">{event?.name}</h1>
            <span className="page-subtitle">Musik & Underholdning</span>
          </div>
        </div>
      </div>

      <div className="page-content">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
          <h2 style={{ fontSize: 18, fontWeight: 700, color: '#1a1a2e' }}>Musikbookinger</h2>
          <button className="btn-primary" onClick={() => setShowForm(!showForm)}>
            {showForm ? 'Luk' : '+ Tilføj booking'}
          </button>
        </div>

        {showForm && (
          <div className="card" style={{ marginBottom: 20, padding: 20 }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12, marginBottom: 12 }}>
              <div className="input-group">
                <label className="input-label">Navn</label>
                <input className="input" value={formData.navn} onChange={e => setFormData({ ...formData, navn: e.target.value })} placeholder="fx DJ Mikkel" />
              </div>
              <div className="input-group">
                <label className="input-label">Type</label>
                <select className="input" style={{ appearance: 'none', cursor: 'pointer' }} value={formData.type} onChange={e => setFormData({ ...formData, type: e.target.value })}>
                  {musikTypes.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div className="input-group">
                <label className="input-label">Pris</label>
                <input className="input" type="number" value={formData.pris} onChange={e => setFormData({ ...formData, pris: parseFloat(e.target.value) })} onFocus={e => e.target.value === '0' && e.target.select()} />
              </div>
              <div className="input-group">
                <label className="input-label">Tidspunkt</label>
                <input className="input" type="time" value={formData.tidspunkt} onChange={e => setFormData({ ...formData, tidspunkt: e.target.value })} />
              </div>
              <div className="input-group">
                <label className="input-label">Telefon</label>
                <input className="input" value={formData.telefon} onChange={e => setFormData({ ...formData, telefon: e.target.value })} />
              </div>
              <div className="input-group">
                <label className="input-label">Link</label>
                <input className="input" value={formData.link} onChange={e => setFormData({ ...formData, link: e.target.value })} />
              </div>
              <div className="input-group">
                <label className="input-label">Kontaktperson</label>
                <input className="input" value={formData.kontakt} onChange={e => setFormData({ ...formData, kontakt: e.target.value })} />
              </div>
              <div className="input-group">
                <label className="input-label">Ansvarlig</label>
                <select className="input" style={{ appearance: 'none', cursor: 'pointer' }} value={formData.assignedTo} onChange={e => setFormData({ ...formData, assignedTo: e.target.value })}>
                  <option value="">Vælg team medlem</option>
                  <option value={currentUser.name}>{currentUser.name} (dig selv)</option>
                  {event?.members?.filter(m => m.name !== currentUser.name).map(m => <option key={m.id} value={m.name}>{m.name}</option>)}
                </select>
              </div>
            </div>
            <div className="input-group">
              <label className="input-label">Beskrivelse</label>
              <textarea className="input" value={formData.beskrivelse} onChange={e => setFormData({ ...formData, beskrivelse: e.target.value })} placeholder="Notater..." style={{ minHeight: 80, resize: 'none' }} />
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
              <button className="btn-primary" onClick={handleAddMusik} style={{ flex: 1 }}>Tilføj booking</button>
              <button className="btn-ghost" onClick={() => setShowForm(false)} style={{ flex: 1 }}>Annuller</button>
            </div>
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12 }}>
          {musikList.map(item => (
            <div key={item.id} className="card" style={{ padding: 16 }}>
              {editingId === item.id ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <input className="input" value={editingValues[item.id]?.navn || item.navn} onChange={e => setEditingValues({ ...editingValues, [item.id]: { ...editingValues[item.id], navn: e.target.value } })} />
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button className="btn-primary" onClick={() => handleUpdate(item.id)} style={{ flex: 1, fontSize: 11, padding: '6px' }}>Gem</button>
                    <button className="btn-ghost" onClick={() => setEditingId(null)} style={{ flex: 1, fontSize: 11, padding: '6px' }}>Annuller</button>
                  </div>
                </div>
              ) : (
                <>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: 8 }}>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: '#1a1a2e' }}>{item.navn}</div>
                      <span style={{ fontSize: 10, background: '#f0eeff', color: '#6c63ff', padding: '2px 8px', borderRadius: 4, display: 'inline-block', marginTop: 4 }}>{item.type}</span>
                    </div>
                    <span style={{ fontSize: 10, fontWeight: 600, color: item.bekræftet ? '#007a58' : '#9a5800', background: item.bekræftet ? '#e8fdf5' : '#fff3e0', padding: '2px 8px', borderRadius: 4, cursor: 'pointer' }} onClick={() => updateMusik(eventId, item.id, { bekræftet: !item.bekræftet })}>
                      {item.bekræftet ? 'bekræftet' : 'Ikke bekræftet'}
                    </span>
                  </div>
                  {item.pris > 0 && <p style={{ fontSize: 11, color: '#1a1a2e', marginBottom: 6 }}>Pris: {item.pris} kr</p>}
                  {item.tidspunkt && <p style={{ fontSize: 11, color: '#DF83A9', marginBottom: 6 }}>Tid: {item.tidspunkt}</p>}
                  {item.kontakt && <p style={{ fontSize: 11, color: '#DF83A9', marginBottom: 6 }}>Kontakt: {item.kontakt}</p>}
                  {item.telefon && <p style={{ fontSize: 11, color: '#DF83A9', marginBottom: 6 }}>Tlf: {item.telefon}</p>}
                  {item.assignedTo && <p style={{ fontSize: 11, color: '#6c63ff', fontWeight: 500, marginBottom: 8 }}>Ansvarlig: {item.assignedTo}</p>}
                  {item.link && <p style={{ fontSize: 11, marginBottom: 8 }}><a href={item.link} target="_blank" rel="noopener noreferrer" style={{ color: '#6c63ff', textDecoration: 'underline' }}>Link</a></p>}
                  {item.beskrivelse && <p style={{ fontSize: 11, color: '#DF83A9', marginBottom: 8, fontStyle: 'italic' }}>{item.beskrivelse}</p>}
                  <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                    <button className="btn-ghost" onClick={() => setEditingId(item.id)} style={{ flex: 1, fontSize: 10, padding: '4px' }}>Rediger</button>
                    {canDelete(eventId) && <button className="btn-ghost" style={{ flex: 1, fontSize: 10, padding: '4px', color: '#ff6b6b' }} onClick={() => handleDelete(item.id)}>Slet</button>}
                  </div>
                </>
              )}
            </div>
          ))}
        </div>

        {musikList.length === 0 && !showForm && (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <p style={{ fontSize: 13, color: '#DF83A9' }}>Ingen musikbookinger endnu</p>
          </div>
        )}
      </div>
    </AppLayout>
  )
}

export function TransportPage() {
  const { eventId } = useParams()
  const { events, transport, addTransport, updateTransport, deleteTransport, canDelete, currentUser } = useApp()
  const [editingId, setEditingId] = useState(null)
  const [editingValues, setEditingValues] = useState({})
  const [showForm, setShowForm] = useState(false)
  const [formData, setFormData] = useState({
    navn: '',
    type: 'Bus',
    fraAdresse: '',
    tilAdresse: '',
    dato: '',
    tidspunkt: '',
    antalPersoner: 0,
    pris: 0,
    telefon: '',
    link: '',
    kontakt: '',
    bekræftet: false,
    assignedTo: ''
  })

  const event = events?.find(e => e.id === eventId)
  const transportList = transport[eventId] || []
  const transportTypes = ['Bus', 'Tog', 'Metro', 'Bil', 'Fly', 'Cykel', 'Taxa', 'Limousine', 'Andet']

  const handleAddTransport = async () => {
    if (!formData.navn) return
    try {
      await addTransport(eventId, formData)
      setFormData({
        navn: '',
        type: 'Bus',
        fraAdresse: '',
        tilAdresse: '',
        dato: '',
        tidspunkt: '',
        antalPersoner: 0,
        pris: 0,
        telefon: '',
        link: '',
        kontakt: '',
        bekræftet: false,
        assignedTo: ''
      })
      setShowForm(false)
    } catch (error) {
      console.error('Error:', error)
    }
  }

  const handleUpdate = async (id) => {
    try {
      await updateTransport(eventId, id, editingValues[id])
      setEditingId(null)
      setEditingValues({})
    } catch (error) {
      console.error('Error:', error)
    }
  }

  const handleDelete = async (id) => {
    try {
      await deleteTransport(eventId, id)
    } catch (error) {
      console.error('Error:', error)
    }
  }

  return (
    <AppLayout>
      <div className="page-header">
        <div className="page-header-row1">
          <div className="page-header-left">
            <h1 className="page-title">Transport & Kørsel</h1>
            <span className="page-subtitle">{event?.name}</span>
          </div>
        </div>
      </div>

      <div className="page-content">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
          <h2 style={{ fontSize: 18, fontWeight: 700, color: '#1a1a2e' }}>Transport bookinger</h2>
          <button className="btn-primary" onClick={() => setShowForm(!showForm)}>
            {showForm ? 'Luk' : '+ Tilføj booking'}
          </button>
        </div>

        {showForm && (
          <div className="card" style={{ marginBottom: 20, padding: 20 }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12, marginBottom: 12 }}>
              <div className="input-group">
                <label className="input-label">Navn</label>
                <input className="input" value={formData.navn} onChange={e => setFormData({ ...formData, navn: e.target.value })} placeholder="fx Bus til venue" />
              </div>
              <div className="input-group">
                <label className="input-label">Type</label>
                <select className="input" style={{ appearance: 'none', cursor: 'pointer' }} value={formData.type} onChange={e => setFormData({ ...formData, type: e.target.value })}>
                  {transportTypes.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div className="input-group">
                <label className="input-label">Fra adresse</label>
                <input className="input" value={formData.fraAdresse} onChange={e => setFormData({ ...formData, fraAdresse: e.target.value })} />
              </div>
              <div className="input-group">
                <label className="input-label">Til adresse</label>
                <input className="input" value={formData.tilAdresse} onChange={e => setFormData({ ...formData, tilAdresse: e.target.value })} />
              </div>
              <div className="input-group">
                <label className="input-label">Dato</label>
                <input className="input" type="date" value={formData.dato} onChange={e => setFormData({ ...formData, dato: e.target.value })} />
              </div>
              <div className="input-group">
                <label className="input-label">Tidspunkt</label>
                <input className="input" type="time" value={formData.tidspunkt} onChange={e => setFormData({ ...formData, tidspunkt: e.target.value })} />
              </div>
              <div className="input-group">
                <label className="input-label">Antal personer</label>
                <input className="input" type="number" value={formData.antalPersoner} onChange={e => setFormData({ ...formData, antalPersoner: parseInt(e.target.value) })} onFocus={e => e.target.value === '0' && e.target.select()} />
              </div>
              <div className="input-group">
                <label className="input-label">Pris</label>
                <input className="input" type="number" value={formData.pris} onChange={e => setFormData({ ...formData, pris: parseFloat(e.target.value) })} onFocus={e => e.target.value === '0' && e.target.select()} />
              </div>
              <div className="input-group">
                <label className="input-label">Telefon</label>
                <input className="input" value={formData.telefon} onChange={e => setFormData({ ...formData, telefon: e.target.value })} />
              </div>
              <div className="input-group">
                <label className="input-label">Link</label>
                <input className="input" value={formData.link} onChange={e => setFormData({ ...formData, link: e.target.value })} />
              </div>
              <div className="input-group">
                <label className="input-label">Kontaktperson</label>
                <input className="input" value={formData.kontakt} onChange={e => setFormData({ ...formData, kontakt: e.target.value })} />
              </div>
              <div className="input-group">
                <label className="input-label">Ansvarlig</label>
                <select className="input" style={{ appearance: 'none', cursor: 'pointer' }} value={formData.assignedTo} onChange={e => setFormData({ ...formData, assignedTo: e.target.value })}>
                  <option value="">Vælg team medlem</option>
                  <option value={currentUser.name}>{currentUser.name} (dig selv)</option>
                  {event?.members?.filter(m => m.name !== currentUser.name).map(m => <option key={m.id} value={m.name}>{m.name}</option>)}
                </select>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
              <button className="btn-primary" onClick={handleAddTransport} style={{ flex: 1 }}>Tilføj booking</button>
              <button className="btn-ghost" onClick={() => setShowForm(false)} style={{ flex: 1 }}>Annuller</button>
            </div>
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12 }}>
          {transportList.map(item => (
            <div key={item.id} className="card" style={{ padding: 16 }}>
              {editingId === item.id ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <input className="input" value={editingValues[item.id]?.navn || item.navn} onChange={e => setEditingValues({ ...editingValues, [item.id]: { ...editingValues[item.id], navn: e.target.value } })} />
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button className="btn-primary" onClick={() => handleUpdate(item.id)} style={{ flex: 1, fontSize: 11, padding: '6px' }}>Gem</button>
                    <button className="btn-ghost" onClick={() => setEditingId(null)} style={{ flex: 1, fontSize: 11, padding: '6px' }}>Annuller</button>
                  </div>
                </div>
              ) : (
                <>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: 8 }}>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: '#1a1a2e' }}>{item.navn}</div>
                      <span style={{ fontSize: 10, background: '#f0eeff', color: '#6c63ff', padding: '2px 8px', borderRadius: 4, display: 'inline-block', marginTop: 4 }}>{item.type}</span>
                    </div>
                    <span style={{ fontSize: 10, fontWeight: 600, color: item.bekræftet ? '#007a58' : '#9a5800', background: item.bekræftet ? '#e8fdf5' : '#fff3e0', padding: '2px 8px', borderRadius: 4, cursor: 'pointer' }} onClick={() => updateTransport(eventId, item.id, { bekræftet: !item.bekræftet })}>
                      {item.bekræftet ? 'bekræftet' : 'Ikke bekræftet'}
                    </span>
                  </div>
                  {item.fraAdresse && <p style={{ fontSize: 11, color: '#1a1a2e', marginBottom: 4 }}>Fra: {item.fraAdresse}</p>}
                  {item.tilAdresse && <p style={{ fontSize: 11, color: '#1a1a2e', marginBottom: 6 }}>Til: {item.tilAdresse}</p>}
                  {item.dato && <p style={{ fontSize: 11, color: '#DF83A9', marginBottom: 2 }}>{new Date(item.dato).toLocaleDateString('da-DK')}{item.tidspunkt && ` kl. ${item.tidspunkt}`}</p>}
                  {item.antalPersoner > 0 && <p style={{ fontSize: 11, color: '#DF83A9', marginBottom: 6 }}>{item.antalPersoner} personer</p>}
                  {item.pris > 0 && <p style={{ fontSize: 11, color: '#1a1a2e', marginBottom: 6 }}>Pris: {item.pris} kr</p>}
                  {item.kontakt && <p style={{ fontSize: 11, color: '#DF83A9', marginBottom: 6 }}>Kontakt: {item.kontakt}</p>}
                  {item.telefon && <p style={{ fontSize: 11, color: '#DF83A9', marginBottom: 6 }}>Tlf: {item.telefon}</p>}
                  {item.assignedTo && <p style={{ fontSize: 11, color: '#6c63ff', fontWeight: 500, marginBottom: 8 }}>Ansvarlig: {item.assignedTo}</p>}
                  {item.link && <p style={{ fontSize: 11, marginBottom: 8 }}><a href={item.link} target="_blank" rel="noopener noreferrer" style={{ color: '#6c63ff', textDecoration: 'underline' }}>Link</a></p>}
                  <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                    <button className="btn-ghost" onClick={() => setEditingId(item.id)} style={{ flex: 1, fontSize: 10, padding: '4px' }}>Rediger</button>
                    {canDelete(eventId) && <button className="btn-ghost" style={{ flex: 1, fontSize: 10, padding: '4px', color: '#ff6b6b' }} onClick={() => handleDelete(item.id)}>Slet</button>}
                  </div>
                </>
              )}
            </div>
          ))}
        </div>

        {transportList.length === 0 && !showForm && (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <p style={{ fontSize: 13, color: '#DF83A9' }}>Ingen transport bookinger endnu</p>
          </div>
        )}
      </div>
    </AppLayout>
  )
}

// Venue Page
export function VenuePage() {
  const { eventId } = useParams()
  const { events, venues, addVenue, updateVenue, deleteVenue, canDelete, currentUser } = useApp()
  const [editingId, setEditingId] = useState(null)
  const [editingValues, setEditingValues] = useState({})
  const [showForm, setShowForm] = useState(false)
  const [formData, setFormData] = useState({
    navn: '',
    adresse: '',
    kontaktNavn: '',
    kontaktTlf: '',
    kontaktEmail: '',
    dato: '',
    tidspunkt: '',
    antalPersoner: 0,
    link: '',
    billede: '',
    notater: '',
    prisPrPerson: 0,
    bekræftet: false,
    assignedTo: ''
  })

  const event = events?.find(e => e.id === eventId)
  const venueList = venues[eventId] || []

  useEffect(() => {
    console.log('VenuePage - venues data:', { eventId, venueList, allVenues: venues })
  }, [venues, eventId])

  const handleAddVenue = async () => {
    console.log('handleAddVenue called with:', { eventId, formData })
    if (!formData.navn) {
      console.log('Form validation failed: navn is empty')
      return
    }
    try {
      console.log('Adding venue:', formData)
      await addVenue(eventId, formData)
      console.log('Venue added successfully')
      setFormData({
        navn: '',
        adresse: '',
        kontaktNavn: '',
        kontaktTlf: '',
        kontaktEmail: '',
        dato: '',
        tidspunkt: '',
        antalPersoner: 0,
        link: '',
        billede: '',
        notater: '',
        prisPrPerson: 0,
        bekræftet: false,
        assignedTo: ''
      })
      setShowForm(false)
    } catch (error) {
      console.error('Error adding venue:', error)
    }
  }

  const handleUpdate = async (id) => {
    try {
      await updateVenue(eventId, id, editingValues[id])
      setEditingId(null)
      setEditingValues({})
    } catch (error) {
      console.error('Error:', error)
    }
  }

  const handleDelete = async (id) => {
    try {
      await deleteVenue(eventId, id)
    } catch (error) {
      console.error('Error:', error)
    }
  }

  return (
    <AppLayout>
      <div className="page-header">
        <div className="page-header-row1">
          <div className="page-header-left">
            <h1 className="page-title">{event?.name}</h1>
            <span className="page-subtitle">Venues & Begivenhed</span>
          </div>
        </div>
      </div>

      <div className="page-content">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
          <h2 style={{ fontSize: 18, fontWeight: 700, color: '#1a1a2e' }}>Steder</h2>
          <button className="btn-primary" onClick={() => setShowForm(!showForm)}>
            {showForm ? 'Luk' : '+ Tilføj venue'}
          </button>
        </div>

        {showForm && (
          <div className="card" style={{ marginBottom: 20, padding: 20 }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12, marginBottom: 12 }}>
              <div className="input-group">
                <label className="input-label">Navn på stedet</label>
                <input className="input" value={formData.navn} onChange={e => setFormData({ ...formData, navn: e.target.value })} placeholder="fx Congress Center" />
              </div>
              <div className="input-group">
                <label className="input-label">Adresse</label>
                <input className="input" value={formData.adresse} onChange={e => setFormData({ ...formData, adresse: e.target.value })} placeholder="fx Vej 1, 1000 København" />
              </div>
              <div className="input-group">
                <label className="input-label">Kontaktperson</label>
                <input className="input" value={formData.kontaktNavn} onChange={e => setFormData({ ...formData, kontaktNavn: e.target.value })} placeholder="fx John Doe" />
              </div>
              <div className="input-group">
                <label className="input-label">Telefon</label>
                <input className="input" value={formData.kontaktTlf} onChange={e => setFormData({ ...formData, kontaktTlf: e.target.value })} placeholder="+45 12 34 56 78" />
              </div>
              <div className="input-group">
                <label className="input-label">Email</label>
                <input className="input" type="email" value={formData.kontaktEmail} onChange={e => setFormData({ ...formData, kontaktEmail: e.target.value })} placeholder="kontakt@venue.dk" />
              </div>
              <div className="input-group">
                <label className="input-label">Dato</label>
                <input className="input" type="date" value={formData.dato} onChange={e => setFormData({ ...formData, dato: e.target.value })} />
              </div>
              <div className="input-group">
                <label className="input-label">Tidspunkt</label>
                <input className="input" type="time" value={formData.tidspunkt} onChange={e => setFormData({ ...formData, tidspunkt: e.target.value })} />
              </div>
              <div className="input-group">
                <label className="input-label">Antal personer</label>
                <input className="input" type="number" value={formData.antalPersoner} onChange={e => setFormData({ ...formData, antalPersoner: parseInt(e.target.value) })} />
              </div>
              <div className="input-group">
                <label className="input-label">Pris pr person (kr)</label>
                <input className="input" type="number" value={formData.prisPrPerson} onChange={e => setFormData({ ...formData, prisPrPerson: parseFloat(e.target.value) || 0 })} />
              </div>
              <div className="input-group">
                <label className="input-label">Pris i alt (beregnet)</label>
                <input className="input" type="number" value={formData.prisPrPerson * formData.antalPersoner || 0} disabled style={{ background: '#f7f5ff', cursor: 'not-allowed' }} />
              </div>
              <div className="input-group">
                <label className="input-label">Link til stedet</label>
                <input className="input" value={formData.link} onChange={e => setFormData({ ...formData, link: e.target.value })} placeholder="https://..." />
              </div>
              <div className="input-group">
                <label className="input-label">Billede (link)</label>
                <input className="input" value={formData.billede} onChange={e => setFormData({ ...formData, billede: e.target.value })} placeholder="https://..." />
              </div>
              <div style={{ gridColumn: '1 / -1' }} className="input-group">
                <label className="input-label">Notater</label>
                <textarea className="input" value={formData.notater} onChange={e => setFormData({ ...formData, notater: e.target.value })} placeholder="Ekstra informationer..." style={{ minHeight: 80, resize: 'none' }} />
              </div>
              <div className="input-group">
                <label className="input-label">Ansvarlig</label>
                <select className="input" style={{ appearance: 'none', cursor: 'pointer' }} value={formData.assignedTo} onChange={e => setFormData({ ...formData, assignedTo: e.target.value })}>
                  <option value="">Vælg team medlem</option>
                  <option value={currentUser.name}>{currentUser.name} (dig selv)</option>
                  {event?.members?.filter(m => m.name !== currentUser.name).map(m => <option key={m.id} value={m.name}>{m.name}</option>)}
                </select>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
              <button className="btn-primary" onClick={handleAddVenue} style={{ flex: 1 }}>Tilføj venue</button>
              <button className="btn-ghost" onClick={() => setShowForm(false)} style={{ flex: 1 }}>Annuller</button>
            </div>
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 12 }}>
          {venueList.map(item => (
            <div key={item.id} className="card" style={{ padding: 16, display: 'flex', flexDirection: 'column' }}>
              {item.billede && (
                <div style={{ width: '100%', height: 180, borderRadius: 8, marginBottom: 12, overflow: 'hidden', background: '#f7f5ff' }}>
                  <img src={item.billede} alt={item.navn} style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={e => e.target.style.display = 'none'} />
                </div>
              )}
              {editingId === item.id ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <input className="input" value={editingValues[item.id]?.navn || item.navn} onChange={e => setEditingValues({ ...editingValues, [item.id]: { ...editingValues[item.id], navn: e.target.value } })} />
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button className="btn-primary" onClick={() => handleUpdate(item.id)} style={{ flex: 1, fontSize: 11, padding: '6px' }}>Gem</button>
                    <button className="btn-ghost" onClick={() => setEditingId(null)} style={{ flex: 1, fontSize: 11, padding: '6px' }}>Annuller</button>
                  </div>
                </div>
              ) : (
                <>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: 8 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 14, fontWeight: 600, color: '#1a1a2e', marginBottom: 2 }}>{item.navn}</div>
                      {item.adresse && <p style={{ fontSize: 11, color: '#DF83A9', marginBottom: 4 }}>ðŸ“ {item.adresse}</p>}
                    </div>
                    <span style={{ fontSize: 10, fontWeight: 600, color: item.bekræftet ? '#007a58' : '#9a5800', background: item.bekræftet ? '#e8fdf5' : '#fff3e0', padding: '2px 8px', borderRadius: 4, cursor: 'pointer', whiteSpace: 'nowrap', marginLeft: 8 }} onClick={() => updateVenue(eventId, item.id, { bekræftet: !item.bekræftet })}>
                      {item.bekræftet ? 'bekræftet' : 'Ikke bekræftet'}
                    </span>
                  </div>
                  {item.dato && <p style={{ fontSize: 11, color: '#1a1a2e', marginBottom: 2 }}>ðŸ“… {new Date(item.dato).toLocaleDateString('da-DK')}{item.tidspunkt && ` kl. ${item.tidspunkt}`}</p>}
                  {item.antalPersoner > 0 && <p style={{ fontSize: 11, color: '#1a1a2e', marginBottom: 2 }}>ðŸ‘¥ {item.antalPersoner} personer</p>}
                  {item.kontaktNavn && <p style={{ fontSize: 11, color: '#DF83A9', marginBottom: 2 }}>Kontakt: {item.kontaktNavn}</p>}
                  {item.kontaktTlf && <p style={{ fontSize: 11, color: '#DF83A9', marginBottom: 2 }}>Tlf: {item.kontaktTlf}</p>}
                  {item.kontaktEmail && <p style={{ fontSize: 11, color: '#DF83A9', marginBottom: 4 }}>Email: {item.kontaktEmail}</p>}
                  {item.prisPrPerson > 0 && (
                    <div style={{ fontSize: 12, color: '#1a1a2e', fontWeight: 600, marginBottom: 4, padding: '8px', background: '#f7f5ff', borderRadius: 6 }}>
                      <p style={{ margin: '0 0 4px 0' }}>ðŸ’° {item.prisPrPerson} kr/person</p>
                      {item.antalPersoner > 0 && <p style={{ margin: 0, color: '#6c63ff' }}>I alt: {item.prisPrPerson * item.antalPersoner} kr</p>}
                    </div>
                  )}
                  {item.notater && <p style={{ fontSize: 11, color: '#DF83A9', marginBottom: 4, fontStyle: 'italic' }}>"{item.notater}"</p>}
                  {item.assignedTo && <p style={{ fontSize: 11, color: '#6c63ff', fontWeight: 500, marginBottom: 8 }}>Ansvarlig: {item.assignedTo}</p>}
                  {item.link && <p style={{ fontSize: 11, marginBottom: 12 }}><a href={item.link} target="_blank" rel="noopener noreferrer" style={{ color: '#6c63ff', textDecoration: 'underline' }}>Besøg website</a></p>}
                  <div style={{ display: 'flex', gap: 8, marginTop: 'auto' }}>
                    <button className="btn-ghost" onClick={() => setEditingId(item.id)} style={{ flex: 1, fontSize: 10, padding: '4px' }}>Rediger</button>
                    {canDelete(eventId) && <button className="btn-ghost" style={{ flex: 1, fontSize: 10, padding: '4px', color: '#ff6b6b' }} onClick={() => handleDelete(item.id)}>Slet</button>}
                  </div>
                </>
              )}
            </div>
          ))}
        </div>

        {venueList.length === 0 && !showForm && (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <p style={{ fontSize: 13, color: '#DF83A9' }}>Ingen venues eller begivenhed tilføjet endnu</p>
          </div>
        )}
      </div>
    </AppLayout>
  )
}

// Team Members Page
export function TeamPage() {
  const { eventId } = useParams()
  const { events, updateEvent, removeMember, currentUser, canDelete } = useApp()
  const [showInvite, setShowInvite] = useState(false)

  const event = events && events.find(e => e.id === eventId)
  const members = event?.members || []

  const handleRoleChange = async (memberId, newRole) => {
    const updatedMembers = members.map(m => m.id === memberId ? { ...m, role: newRole } : m)
    await updateEvent(eventId, { members: updatedMembers })
  }

  const getRoleLabel = (role) => {
    switch (role) {
      case 'owner': return 'Arrangør'
      case 'co-owner': return 'Med-ansvarlig'
      case 'helper': return 'Medhjælper'
      default: return role
    }
  }

  return (
    <AppLayout>
      <div className="page-header">
        <div className="page-header-row1">
          <div className="page-header-left">
            <h1 className="page-title">{event?.name}</h1>
            <span className="page-subtitle">Team</span>
          </div>
          {canDelete(eventId) && (
            <button className="btn-primary" onClick={() => setShowInvite(true)}>
              <svg width="11" height="11" viewBox="0 0 11 11" fill="none"><path d="M5.5 1v9M1 5.5h9" stroke="white" strokeWidth="1.5" strokeLinecap="round"/></svg>
              inviter
            </button>
          )}
        </div>
      </div>

      <div className="page-content">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 14 }}>
          {members.map(member => (
            <div key={member.id} className="card" style={{ padding: 20 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                <div style={{ width: 48, height: 48, borderRadius: '50%', background: event?.color || '#6c63ff', color: '#fff', fontSize: 14, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {member.name.split(' ').map(n => n[0]).join('')}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#1a1a2e' }}>{member.name}</div>
                  <div style={{ fontSize: 11, color: '#DF83A9', marginTop: 4 }}>{getRoleLabel(member.role)}</div>
                </div>
              </div>

              <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
                {canDelete(eventId) && member.role !== 'owner' && (
                  <>
                    <select value={member.role} onChange={e => handleRoleChange(member.id, e.target.value)} style={{ flex: 1, padding: '8px 10px', fontSize: 11, borderRadius: 8, border: '1px solid #e0e0e0', background: '#fff', cursor: 'pointer', fontFamily: 'inherit' }}>
                      <option value="helper">Medhjælper</option>
                      <option value="co-owner">Med-ansvarlig</option>
                    </select>
                    <button onClick={() => removeMember(eventId, member.id)} style={{ padding: '8px 12px', background: '#ffe4e4', border: '1px solid #cc2020', borderRadius: 8, color: '#cc2020', fontSize: 11, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>fjern</button>
                  </>
                )}
                {canDelete(eventId) && member.role === 'owner' && (
                  <div style={{ fontSize: 11, color: '#DF83A9', fontStyle: 'italic' }}>arrangør kan ikke fjernes</div>
                )}
              </div>
            </div>
          ))}
        </div>

        {members.length === 0 && (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <p style={{ fontSize: 13, color: '#DF83A9' }}>ingen teammedlemmer endnu</p>
          </div>
        )}
      </div>

      {showInvite && <InviteModal eventId={eventId} event={event} onClose={() => setShowInvite(false)} />}
    </AppLayout>
  )
}

// Files Page
export function FilesPage() {
  const { eventId } = useParams()
  const { events, files, addFile, deleteFile, canDelete, showToast } = useApp()
  const [uploading, setUploading] = useState(false)
  const [dragActive, setDragActive] = useState(false)
  const [error, setError] = useState(null)

  const event = events && events.find(e => e.id === eventId)
  const eventFiles = files[eventId] || []

  const handleFileUpload = async (file) => {
    if (!file) return
    try {
      setError(null)
      setUploading(true)
      console.log('Starting file upload:', file.name)

      const filename = `${Date.now()}_${file.name}`
      const storageRef = ref(storage, `files/${eventId}/${filename}`)
      console.log('Storage reference created:', storageRef.fullPath)

      await uploadBytes(storageRef, file)
      console.log('File uploaded to storage')

      const downloadURL = await getDownloadURL(storageRef)
      console.log('Download URL obtained:', downloadURL)

      const fileData = {
        name: file.name,
        type: file.type,
        size: file.size,
        url: downloadURL,
        uploadedAt: new Date().toISOString(),
      }

      await addFile(eventId, fileData)
      console.log('File saved to Firestore')
      showToast('Fil uploadet! ✓')
    } catch (error) {
      console.error('Error uploading file:', error)
      const errorMsg = error.code ? `Fejl: ${error.code}` : 'Der skete en fejl ved upload'
      setError(errorMsg)
      showToast(errorMsg)
    } finally {
      setUploading(false)
    }
  }

  const handleDrag = (e) => {
    e.preventDefault()
    e.stopPropagation()
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true)
    } else if (e.type === 'dragleave') {
      setDragActive(false)
    }
  }

  const handleDrop = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)

    const files = e.dataTransfer.files
    if (files && files[0]) {
      handleFileUpload(files[0])
    }
  }

  const getFileIcon = (fileType) => {
    if (fileType.includes('pdf')) return '📄'
    if (fileType.includes('image')) return '🖼️'
    return '📎'
  }

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i]
  }

  return (
    <AppLayout>
      <div className="page-header">
        <div className="page-header-row1">
          <div className="page-header-left">
            <h1 className="page-title">{event?.name}</h1>
            <span className="page-subtitle">Filer</span>
          </div>
        </div>
      </div>

      <div className="page-content">
        <div
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          style={{
            border: dragActive ? `2px dashed ${event?.color || '#6c63ff'}` : '2px dashed #e0d5ff',
            borderRadius: 12,
            padding: 32,
            textAlign: 'center',
            background: dragActive ? 'rgba(108, 99, 255, 0.05)' : '#f7f5ff',
            cursor: 'pointer',
            transition: 'all 0.2s',
            marginBottom: 24,
          }}
        >
          <input
            type="file"
            id="file-input"
            onChange={e => e.target.files && handleFileUpload(e.target.files[0])}
            style={{ display: 'none' }}
            multiple={false}
            accept=".pdf,.png,.jpg,.jpeg,.gif,.webp,.doc,.docx,.xls,.xlsx,.ppt,.pptx"
          />
          <label htmlFor="file-input" style={{ cursor: 'pointer', display: 'block' }}>
            <div style={{ fontSize: 32, marginBottom: 8 }}>📤</div>
            <div style={{ fontSize: 14, fontWeight: 600, color: '#1a1a2e', marginBottom: 4 }}>Drag filer her eller klik for at vælge</div>
            <div style={{ fontSize: 12, color: '#DF83A9' }}>PDF'er, billeder og dokumenter understøttes</div>
            {uploading && <div style={{ fontSize: 12, color: '#6c63ff', marginTop: 8, fontWeight: 500 }}>Uploader...</div>}
            {error && <div style={{ fontSize: 12, color: '#ff6b6b', marginTop: 8, fontWeight: 500 }}>❌ {error}</div>}
          </label>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <h2 style={{ fontSize: 18, fontWeight: 700, color: '#1a1a2e' }}>Uploadede filer ({eventFiles.length})</h2>
        </div>

        {eventFiles.length > 0 ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 14 }}>
            {eventFiles.map(file => {
              const isImage = file.type && file.type.includes('image')
              return (
                <div key={file.id} className="card" style={{ padding: 16, display: 'flex', flexDirection: 'column' }}>
                  {isImage && (
                    <div style={{ width: '100%', height: 150, borderRadius: 8, background: '#f7f5ff', marginBottom: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                      <img src={file.url} alt={file.name} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'cover' }} />
                    </div>
                  )}
                  {!isImage && (
                    <div style={{ fontSize: 36, marginBottom: 8 }}>
                      {getFileIcon(file.type)}
                    </div>
                  )}

                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: '#1a1a2e', marginBottom: 4, wordBreak: 'break-word' }}>{file.name}</div>
                    <div style={{ fontSize: 11, color: '#DF83A9', marginBottom: 8 }}>
                      {formatFileSize(file.size)}
                    </div>
                    {file.uploadedAt && (
                      <div style={{ fontSize: 10, color: '#DF83A9' }}>
                        {new Date(file.uploadedAt).toLocaleDateString('da-DK')}
                      </div>
                    )}
                  </div>

                  <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                    <a
                      href={file.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        flex: 1,
                        padding: '8px 12px',
                        background: event?.color || '#6c63ff',
                        color: '#fff',
                        border: 'none',
                        borderRadius: 6,
                        fontSize: 11,
                        fontWeight: 600,
                        cursor: 'pointer',
                        textAlign: 'center',
                        textDecoration: 'none',
                        fontFamily: 'inherit',
                      }}
                    >
                      download
                    </a>
                    {canDelete(eventId) && (
                      <button
                        onClick={() => deleteFile(eventId, file.id)}
                        style={{
                          flex: 1,
                          padding: '8px 12px',
                          background: '#ffe4e4',
                          color: '#ff6b6b',
                          border: '1px solid #ff6b6b',
                          borderRadius: 6,
                          fontSize: 11,
                          fontWeight: 600,
                          cursor: 'pointer',
                          fontFamily: 'inherit',
                        }}
                      >
                        slet
                      </button>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <p style={{ fontSize: 13, color: '#DF83A9' }}>ingen filer uploadet endnu</p>
          </div>
        )}
      </div>
    </AppLayout>
  )
}




